// Atlas API client — talks to the FastAPI backend at backend/apps/api.
// Loaded as a Babel-standalone <script>; exposes window.AtlasApi.

// Production builds inject `window.__ATLAS_API_BASE__` from index.html so
// the SPA can find the FastAPI service without a build step. Local dev
// leaves the global unset → falls through to localhost:8001. A user-set
// localStorage override always wins (handy for switching environments).
const DEFAULT_BASE =
  (typeof window !== 'undefined' && window.__ATLAS_API_BASE__) || 'http://localhost:8001';
const DEFAULT_TOKEN = 'atlas-admin-local';
const TOKEN_KEY = 'atlas.token';
const BASE_KEY = 'atlas.baseUrl';
const ACTIVE_USER_KEY = 'atlas.activeUserId';
const SCOPE_KEY = 'atlas.allTeams';

const getBaseUrl = () =>
  (typeof localStorage !== 'undefined' && localStorage.getItem(BASE_KEY)) || DEFAULT_BASE;

// ── Team scope (My team vs All teams) ────────────────────────────────────
// List calls hard-default my_team=true (a dealmaker's working set). The
// "All teams" toggle flips one global so a non-admin can see firm-wide
// records read-only — e.g. to find a deal misassigned to another team, or
// unowned rows that team-scoping otherwise hides. When on, we force
// my_team=false on every scoped list call so the backend skips filtering.
const isAllTeams = () => {
  try { return localStorage.getItem(SCOPE_KEY) === '1'; } catch (_) { return false; }
};
const setAllTeams = (on) => {
  try { localStorage.setItem(SCOPE_KEY, on ? '1' : '0'); } catch (_) {}
};
// Role-based first-run default, called once whoami resolves: admins live
// firm-wide (the backend already ignores my_team for them — this makes the
// header pill say so instead of claiming "My team" over firm-wide data),
// and marketing works the lead pool across every team, not one dealteam.
// Only applies while the user has never touched the toggle; an explicit
// flip is stored as '1'/'0' and always wins.
const seedAllTeamsDefault = (role) => {
  try {
    if (localStorage.getItem(SCOPE_KEY) !== null) return;
    const r = (role || '').toLowerCase();
    if (r === 'admin' || r === 'marketing') setAllTeams(true);
  } catch (_) {}
};
const withScope = (p) => (isAllTeams() ? { ...p, my_team: false } : p);

// Token priority for outgoing requests:
//   1. Supabase access_token from the logged-in user (set by login.jsx).
//   2. A manual override in localStorage['atlas.token'] (Tweaks panel /
//      debugging). Useful for hitting a legacy admin/editor/viewer token.
//   3. The hardcoded local-dev default (never used in production).
const getToken = () => {
  try {
    const session = window.AtlasAuth?.getSession?.();
    if (session?.access_token) return session.access_token;
  } catch (_) {}
  return (typeof localStorage !== 'undefined' && localStorage.getItem(TOKEN_KEY)) || DEFAULT_TOKEN;
};

// Bounce-to-login is the nuclear option — it clears localStorage and
// reloads, which then propagates the sign-out to every other Atlas tab
// via the storage event in AuthGate. We've seen this fire spuriously
// during deploys (the API restarts, returns a transient 401, every tab
// signs out simultaneously). Two guards to prevent that:
//
//   1. Only bounce when the refresh-token endpoint REJECTED the refresh.
//      If refresh succeeded but the retry still 401s, that's an API-side
//      transient (deploy race, validation cache cold, etc.) — let the
//      individual request fail rather than killing the session.
//   2. Require 3 consecutive "refresh failed" events before bouncing.
//      Anything below that is just noise — Supabase Auth might be
//      having a moment.
//
// The counter resets to 0 on any successful response (see bottom of
// apiFetch). Single transient blip → no bounce. Sustained failure →
// genuine sign-out.
let _bouncedToLogin = false;
let _consecutiveRefreshFailures = 0;
const _BOUNCE_THRESHOLD = 3;

const _bounceToLogin = (path, status, refreshFailed) => {
  if (_bouncedToLogin) return;

  if (!refreshFailed) {
    // Refresh succeeded but the retry still 401'd. Almost always API-side
    // transient. Log and propagate — don't kill the session.
    try { console.warn(`Atlas: ${status} on ${path} after successful refresh — propagating as request error rather than signing out`); } catch (_) {}
    return;
  }

  _consecutiveRefreshFailures += 1;
  if (_consecutiveRefreshFailures < _BOUNCE_THRESHOLD) {
    try { console.warn(`Atlas: ${status} on ${path}, refresh failed (${_consecutiveRefreshFailures}/${_BOUNCE_THRESHOLD}) — holding off`); } catch (_) {}
    return;
  }

  _bouncedToLogin = true;
  try {
    console.warn(`Atlas: ${status} on ${path} — session genuinely expired after ${_BOUNCE_THRESHOLD} failed refreshes, returning to login`);
    window.AtlasAuth?.clear?.();
  } catch (_) {}
  setTimeout(() => window.location.reload(), 50);
};

// Reset the failure counter after any 2xx — signals the session is fine
// and earlier 401s were transient.
const _markAuthSuccess = () => {
  if (_consecutiveRefreshFailures !== 0) {
    try { console.info(`Atlas: auth recovered, resetting failure counter (was ${_consecutiveRefreshFailures})`); } catch (_) {}
  }
  _consecutiveRefreshFailures = 0;
};

const setToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};
const setBaseUrl = (url) => {
  if (url) localStorage.setItem(BASE_KEY, url);
  else localStorage.removeItem(BASE_KEY);
};

// API responses can carry HTML-encoded strings (DealCloud encodes things like
// "M&A" as "M&amp;A"). Decode every string in the payload at the fetch boundary
// so views render readable text without per-field workarounds.
const HTML_ENTITY_RE = /&(?:amp|lt|gt|quot|#39|apos|nbsp|#(\d+)|#x([0-9a-fA-F]+));/g;
const decodeHtmlString = (s) => {
  if (!s || s.indexOf('&') === -1) return s;
  return s.replace(HTML_ENTITY_RE, (m, dec, hex) => {
    if (dec) return String.fromCodePoint(parseInt(dec, 10));
    if (hex) return String.fromCodePoint(parseInt(hex, 16));
    return { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&apos;': "'", '&nbsp;': ' ' }[m] || m;
  });
};
const decodeDeep = (v) => {
  if (typeof v === 'string') return decodeHtmlString(v);
  if (Array.isArray(v)) { for (let i = 0; i < v.length; i++) v[i] = decodeDeep(v[i]); return v; }
  if (v && typeof v === 'object') { for (const k in v) v[k] = decodeDeep(v[k]); return v; }
  return v;
};

// Pick the token for an outgoing request, refreshing proactively if the
// Supabase access_token is within 60s of expiry. This is async because
// the refresh involves a network call to Supabase; callers wrap their
// whole fetch in `await tokenForRequest()`.
async function tokenForRequest() {
  try {
    if (window.AtlasAuth?.getValidSession) {
      const session = await window.AtlasAuth.getValidSession(60);
      if (session?.access_token) return session.access_token;
    }
  } catch (_) {}
  // Fall through to legacy bearer token (Tweaks panel / scripts).
  return getToken();
}

// ── In-process GET cache + request dedup ──────────────────────────────
// Two related layers, both opt-in via the `cacheMs` option on apiFetch:
//
//  1. Dedup: if two callers fire the same GET in parallel, they share a
//     single in-flight Promise. The redundant `limit=5000` engagements
//     calls we saw in the network tab disappear because the second
//     caller awaits the first's result instead of opening its own
//     connection.
//
//  2. TTL: results are kept for `cacheMs` ms (default 30s for GETs).
//     A subsequent caller within the window resolves synchronously
//     against the cached value. Lookup tables (stages, members, fields)
//     pass a longer cacheMs so they don't re-fetch on every navigation.
//
// Any non-GET request (POST/PUT/PATCH/DELETE) invalidates the entire
// cache — write-then-read stays correct because the next list/detail
// GET goes to the server fresh.
const _inflight = new Map();   // url → Promise (dedup)
const _ttlCache = new Map();   // url → { value, expiresAt } (TTL)
const _cacheKey = (path, opts) => {
  // Distinguish requests by their X-Active-User too so an admin who
  // switches users doesn't see another user's cached payload.
  const u = (typeof localStorage !== 'undefined' && localStorage.getItem(ACTIVE_USER_KEY)) || '';
  return `${u}::${path}`;
};
// Writes to these entity roots also invalidate the listed roots — grids embed
// denormalized fields from related entities (engagement rows carry
// company_name, referrals denormalize contacts, engagement rows carry
// note_count, …). A write to any root NOT in this map falls back to a full
// clear: unknown writes (industries, team, views…) can touch anything.
// '/api/records' (the record summaries) rides on every root a record card
// reads; the vault and data room write under /api/companies, and tasks,
// attachments and buyers writes clear everything already.
const _INVALIDATE_RELATED = {
  // Marking a company acquired exits its deals and files a note; a Company
  // sold exit flips the company and files notes (buyer_acquisitions close-out).
  '/api/companies':   ['/api/engagements', '/api/referrals', '/api/buyers', '/api/search', '/api/notes', '/api/records'],
  '/api/contacts':    ['/api/referrals', '/api/notes', '/api/search', '/api/records'],
  '/api/engagements': ['/api/buyers', '/api/search', '/api/notes', '/api/companies', '/api/records'],
  '/api/notes':       ['/api/engagements', '/api/companies', '/api/records'],
  // A tracker upload creates companies, people, buyer rows and notes.
  '/api/outreach':    ['/api/buyers', '/api/companies', '/api/contacts', '/api/notes', '/api/search', '/api/records'],
  // Beacons (phone landing, record views) change no data — without this entry
  // every one would fall through to a full cache clear.
  '/api/ui-events':   [],
};

const _invalidateCache = (writePath) => {
  // Scoped invalidation: one inline cell edit used to blow away EVERY cached
  // GET (all grids + all long-TTL lookup tables), forcing full refetches on
  // the next navigation. Clear only the written entity's keys + the roots
  // that embed its data; anything unrecognized still clears everything.
  const root = /^(\/api\/[^/?]+)/.exec(writePath || '')?.[1];
  const related = root && _INVALIDATE_RELATED[root];
  if (!related) {
    _ttlCache.clear();
    return;
  }
  const roots = [root, ...related];
  for (const key of [..._ttlCache.keys()]) {
    const path = key.slice(key.indexOf('::') + 2);
    if (roots.some((r) => path === r || path.startsWith(r + '/') || path.startsWith(r + '?'))) {
      _ttlCache.delete(key);
    }
  }
  // In-flight requests can still resolve, but new ones won't hit cache.
};

// Writes that change what a record page's cards show; views/record_summary.jsx
// refetches the record on screen (the summary is never cached, so clearing the
// cache can't refresh it). The read-POSTs are not writes: RecordNotes re-asks
// for previews in rounds, and each would cost a full summary.
const _RECORD_WRITE_ROOTS = ['/api/notes', '/api/engagements', '/api/companies', '/api/contacts', '/api/outreach',
  '/api/tasks', '/api/attachments', '/api/buyers', '/api/company-facts', '/api/sellers', '/api/comps', '/api/company-signals',
  '/api/blast'];
const _RECORD_READ_POSTS = ['/api/notes/ai-previews', '/api/tasks/parse'];
// The person page's Programs drawer writes these; its Programs card reads them
// (summary.programs): referral and GPP partners, event attendance.
const _PROGRAM_WRITE_ROOTS = ['/api/referral-partners', '/api/gpp-partners', '/api/lead-events'];
const _announceWrite = (path, method) => {
  const m = String(method || 'GET').toUpperCase();
  if (m === 'GET') return;
  const p = String(path || '').split('?')[0];
  if (_RECORD_READ_POSTS.includes(p)) return;
  const root = /^(\/api\/[^/?]+)/.exec(p)?.[1];
  if (!root || !(_RECORD_WRITE_ROOTS.includes(root) || _PROGRAM_WRITE_ROOTS.includes(root))) return;
  try {
    window.dispatchEvent(new CustomEvent('atlas-record-write', { detail: { root, path, method: m } }));
  } catch (_) { /* a listener's failure never fails the write */ }
};

async function apiFetch(path, opts = {}) {
  // When the user has clicked the "Live API" pill into Mock mode, short-circuit
  // every API call through the in-memory mock fixtures (mock_api.jsx). No network
  // request fires — handy for demos and for working when the API is offline.
  if (window.AtlasMockApi && window.AtlasMockApi.forceMock) {
    const mocked = decodeDeep(window.AtlasMockApi.handle(path, opts));
    _announceWrite(path, opts.method);
    return mocked;
  }

  const method = (opts.method || 'GET').toUpperCase();
  const isGet = method === 'GET';
  const cacheMs = isGet ? (opts.cacheMs ?? 30000) : 0;
  const key = isGet ? _cacheKey(path, opts) : null;

  // 1. Serve from TTL cache if fresh.
  if (key && cacheMs > 0) {
    const hit = _ttlCache.get(key);
    if (hit && hit.expiresAt > Date.now()) {
      return hit.value;
    }
    // 2. Dedup: piggy-back on an in-flight identical request.
    const inflight = _inflight.get(key);
    if (inflight) return inflight;
  }
  // Auto-serialize plain-object bodies. The Fetch spec stringifies any
  // unknown body via `String(value)` → "[object Object]", which the server
  // then rejects with a 422 JSON-decode error. FormData / URLSearchParams /
  // Blob have their own contentType handling, so leave those alone.
  let { body } = opts;
  if (body && typeof body === 'object'
        && !(body instanceof FormData)
        && !(body instanceof URLSearchParams)
        && !(body instanceof Blob)
        && !(body instanceof ArrayBuffer)) {
    body = JSON.stringify(body);
  }

  // fetch() has no native timeout — a wedged connection (wifi drop, Render
  // instance cycling mid-request) never settles. Combined with the in-flight
  // dedup below, one hung GET used to poison its cache key forever: every
  // later identical request piggy-backed on the dead promise and the view
  // could never load again without a hard page refresh. Abort at the
  // headers-arrival phase (where connection stalls actually happen); writes
  // get longer so attachment uploads survive slow links.
  const timeoutMs = opts.timeoutMs ?? (isGet ? 30000 : 120000);
  const sendOnce = async (token) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    };
    const activeUser = (typeof localStorage !== 'undefined' && localStorage.getItem(ACTIVE_USER_KEY)) || null;
    if (activeUser) headers['X-Active-User'] = activeUser;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      return await fetch(`${getBaseUrl()}${path}`, { ...opts, headers, body, signal: ctrl.signal });
    } finally {
      clearTimeout(timer);
    }
  };

  // Wrap the actual network work in a promise we can register as the
  // in-flight entry. Concurrent callers piggy-back on this same promise.
  //
  // Transient-network retry: `fetch()` rejects with a TypeError ("Failed
  // to fetch") for any connection-level failure — Render restarting an
  // instance during a deploy, brief wifi drop, the user's VPN renegotiating.
  // 99% of these clear in <300ms. One retry handles the common case
  // without surfacing the error to the user (or spamming Sentry).
  // Persistent failures still throw on the second attempt, where existing
  // error handling takes over.
  // The ENTIRE request pipeline — network retry, 401 refresh, decode, TTL
  // store — runs inside this one promise, and that same promise is what
  // concurrent callers piggy-back on. Piggy-backers therefore get the
  // identical decoded value (they used to get a raw un-decoded clone that
  // silently resolved null on errors), and the finally-cleanup guarantees
  // the in-flight entry can never outlive the request, however it ends.
  const run = async () => {
    const token = await tokenForRequest();
    let res;
    try {
      res = await sendOnce(token);
    } catch (err) {
      // TypeError = connection-level failure; AbortError = our own timeout.
      // Both clear in one retry when the cause is an instance cycling or a
      // wifi blip — but only replay GETs: a write may have reached the
      // server before the connection died. The isGet gate used to cover only
      // AbortError, so every "Failed to fetch" write fired a second time
      // 300ms later — visible in the prod logs as paired 500s on the same
      // PATCH (2026-08-31). A dropped write now surfaces once, to the user,
      // instead of being silently applied twice.
      if (isGet && (err instanceof TypeError || err?.name === 'AbortError')) {
        await new Promise((r) => setTimeout(r, 300));
        res = await sendOnce(token);
      } else {
        throw err;
      }
    }
    // 503 = the instance is restarting (deploy window). One delayed retry
    // rides it out for reads; writes surface the error immediately.
    if (isGet && res.status === 503) {
      await new Promise((r) => setTimeout(r, 800));
      res = await sendOnce(token);
    }

    // On 401, try ONE refresh-and-retry before giving up. Handles the case
    // where the access_token expired between our pre-check and the server
    // receiving it, or where the server has a slightly different clock.
    let refreshFailed = false;
    if (res.status === 401) {
      const usingSupabase = !!window.AtlasAuth?.getSession?.()?.access_token;
      if (usingSupabase && window.AtlasAuth?.refreshSession) {
        const refreshed = await window.AtlasAuth.refreshSession();
        if (refreshed?.access_token) {
          res = await sendOnce(refreshed.access_token);
        } else {
          refreshFailed = true;
        }
      } else {
        refreshFailed = true;
      }
    }

    // Still 401/403 after the refresh attempt → MAYBE the session is dead.
    // _bounceToLogin decides whether to actually sign out — it requires the
    // refresh to have failed AND multiple consecutive failures. A single
    // transient 401 (e.g. API restart during deploy) no longer kills the
    // session across tabs.
    if (res.status === 401 || (res.status === 403 && path.startsWith('/api/'))) {
      const usingSupabase = !!window.AtlasAuth?.getSession?.()?.access_token;
      if (usingSupabase) {
        _bounceToLogin(path, res.status, refreshFailed);
      }
    } else if (res.ok) {
      // Any 2xx clears the consecutive-failure counter — earlier 401s were
      // transient and we've recovered.
      _markAuthSuccess();
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const err = new Error(`${res.status} ${res.statusText}: ${text}`);
      err.status = res.status;
      throw err;
    }
    if (res.status === 204) return null;
    const value = decodeDeep(await res.json());
    if (key && cacheMs > 0) {
      _ttlCache.set(key, { value, expiresAt: Date.now() + cacheMs });
    }
    // Any write invalidates the read cache so the next list reflects truth —
    // scoped to the written entity's roots (see _INVALIDATE_RELATED).
    if (!isGet && res.ok) { _invalidateCache(path); _announceWrite(path, method); }
    return value;
  };

  const workPromise = run().finally(() => { if (key) _inflight.delete(key); });
  if (key && cacheMs > 0) _inflight.set(key, workPromise);
  return workPromise;
}

// Server-side file export: hit the endpoint, read the blob, trigger the
// download via window.downloadBlob (table_helpers.jsx). A plain <a href>
// can't carry the Authorization header, so it must be fetch → blob. Resolves
// to {truncated} — true when the server hit its export row cap (X-Truncated),
// so callers can warn instead of letting a truncated file read as complete.
async function exportFetch(path, body, method = 'POST', fallbackName = 'export.csv') {
  const token = await tokenForRequest();
  const headers = { Authorization: `Bearer ${token}` };
  const activeUser = (typeof localStorage !== 'undefined' && localStorage.getItem(ACTIVE_USER_KEY)) || null;
  if (activeUser) headers['X-Active-User'] = activeUser;
  const init = { method, headers };
  if (method !== 'GET') {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body || {});
  }
  const res = await fetch(`${getBaseUrl()}${path}`, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  const blob = await res.blob();
  const m = /filename="?([^";]+)"?/.exec(res.headers.get('Content-Disposition') || '');
  // Native (iOS shell) saves via the share sheet and returns a promise —
  // wait for it so a failed write isn't reported as a finished export.
  const saving = window.downloadBlob(blob, m && m[1], fallbackName);
  if (saving && saving.then) await saving;
  return { truncated: res.headers.get('X-Truncated') === '1' };
}

// An endpoint that answers with a whole HTML document (the daily-report
// emails), read as text for the phone reader (mobile/shell.jsx) instead of
// saved like exportFetch's downloads. Same auth headers, one refresh-and-retry
// on a 401 like apiFetch. Resolves to {html, filename}; rejects with
// err.status set so the caller can tell "no access" (403) from a blip.
async function fetchHtmlDoc(path) {
  if (window.AtlasMockApi && window.AtlasMockApi.forceMock) {
    const err = new Error('Not available in mock mode');
    err.status = 0;
    throw err;
  }
  const send = async (token) => {
    const headers = { Authorization: `Bearer ${token}` };
    const activeUser = (typeof localStorage !== 'undefined' && localStorage.getItem(ACTIVE_USER_KEY)) || null;
    if (activeUser) headers['X-Active-User'] = activeUser;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 30000);
    try {
      return await fetch(`${getBaseUrl()}${path}`, { headers, signal: ctrl.signal });
    } finally {
      clearTimeout(timer);
    }
  };
  let res = await send(await tokenForRequest());
  if (res.status === 401 && window.AtlasAuth?.getSession?.()?.access_token && window.AtlasAuth?.refreshSession) {
    const refreshed = await window.AtlasAuth.refreshSession();
    if (refreshed?.access_token) res = await send(refreshed.access_token);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(`${res.status} ${res.statusText}: ${text}`);
    err.status = res.status;
    throw err;
  }
  const m = /filename="?([^";]+)"?/.exec(res.headers.get('Content-Disposition') || '');
  return { html: await res.text(), filename: m ? m[1] : null };
}

// Ask Atlas streaming turn: POST to /chat/stream and read server-sent events
// off the response body. `onStatus(label)` fires for each live progress line
// ("Searching companies…"); resolves with the same payload as the plain chat
// endpoint once the {type:'done'} event arrives. Falls back to the blocking
// endpoint when streaming isn't available (mock mode, no res.body, or an API
// that predates /chat/stream mid-deploy).
// Stays on the WebView's own fetch in the iOS shell too: Capacitor's
// CapacitorHttp patch (off in atlas-mobile/capacitor.config.ts — keep it off)
// buffers whole responses, which would turn the live status into one wait.
// POST that answers with server-sent events: {type:'status', label} lines
// (passed to onStatus as they arrive), then one {type:'done', ...} — the
// resolved value — or {type:'error', message}. Mock mode resolves the same
// path through the mock router (no stream).
async function postEventStream(path, body, onStatus) {
  if (window.AtlasMockApi && window.AtlasMockApi.forceMock) {
    return apiFetch(path, { method: 'POST', body: JSON.stringify(body) });
  }
  const token = await tokenForRequest();
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const activeUser = (typeof localStorage !== 'undefined' && localStorage.getItem(ACTIVE_USER_KEY)) || null;
  if (activeUser) headers['X-Active-User'] = activeUser;
  const res = await fetch(`${getBaseUrl()}${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const chunk = await reader.read();
    if (chunk.done) break;
    buf += decoder.decode(chunk.value, { stream: true });
    let sep;
    while ((sep = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const line = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      let ev;
      try { ev = JSON.parse(line.slice(5)); } catch (_) { continue; }
      if (ev.type === 'status') { if (onStatus) onStatus(ev.label); }
      else if (ev.type === 'error') throw new Error(ev.message || 'Something went wrong — try again.');
      else if (ev.type === 'done') return decodeDeep(ev);
    }
  }
  throw new Error('The connection dropped — try again.');
}

async function assistantChatStream(body, onStatus) {
  if (window.AtlasMockApi && window.AtlasMockApi.forceMock) {
    return apiFetch('/api/assistant/chat', { method: 'POST', body: JSON.stringify(body) });
  }
  const token = await tokenForRequest();
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const activeUser = (typeof localStorage !== 'undefined' && localStorage.getItem(ACTIVE_USER_KEY)) || null;
  if (activeUser) headers['X-Active-User'] = activeUser;
  const res = await fetch(`${getBaseUrl()}/api/assistant/chat/stream`, {
    method: 'POST', headers, body: JSON.stringify(body),
  });
  if (res.status === 404 || res.status === 405) {
    return apiFetch('/api/assistant/chat', { method: 'POST', body: JSON.stringify(body) });
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  if (!res.body || !res.body.getReader) {
    return apiFetch('/api/assistant/chat', { method: 'POST', body: JSON.stringify(body) });
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let done = null;
  for (;;) {
    const chunk = await reader.read();
    if (chunk.done) break;
    buf += decoder.decode(chunk.value, { stream: true });
    let sep;
    while ((sep = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      const line = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      let ev;
      try { ev = JSON.parse(line.slice(5)); } catch (_) { continue; }
      if (ev.type === 'status') {
        if (onStatus) onStatus(ev.label);
      } else if (ev.type === 'error') {
        throw new Error(ev.message || 'The assistant is unavailable right now.');
      } else if (ev.type === 'done') {
        done = ev;
      }
    }
  }
  if (!done) throw new Error('The assistant connection dropped — try again.');
  _invalidateCache('/api/assistant/chat');
  return decodeDeep(done);
}

const qs = (params) => {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    // Arrays serialize as repeated params (?industry_id=a&industry_id=b) —
    // FastAPI's list[str] Query style.
    if (Array.isArray(v)) v.forEach((x) => { if (x !== undefined && x !== null && x !== '') sp.append(k, x); });
    else sp.set(k, v);
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
};

// Extensions browsers render inline (PDF viewer, images, plain text). Office
// files are not in this list on purpose — a tab can't show them. No SVG: an
// SVG opened as a page runs its scripts.
const ATTACHMENT_INLINE_RE = /\.(pdf|png|jpe?g|gif|webp|bmp|txt)$/i;
// The type an inline file is opened as, from its name — never the type it
// was stored with. Files come from outside (owner emails, data-room
// uploads); one stored as text/html would otherwise run as a page on the
// Atlas origin, next to the signed-in session.
const ATTACHMENT_INLINE_TYPES = {
  pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', bmp: 'image/bmp', txt: 'text/plain;charset=utf-8',
};
const inlineBlobType = (name) => ATTACHMENT_INLINE_TYPES[(String(name || '').split('.').pop() || '').toLowerCase()] || 'application/octet-stream';

const AtlasApi = {
  // session
  getToken, setToken, getBaseUrl, setBaseUrl, isAllTeams, setAllTeams, seedAllTeamsDefault,
  health: () => fetch(`${getBaseUrl()}/health`).then((r) => r.ok),
  // /api/auth/whoami is the boot identity probe — cache it for 60s so
  // every view that wants the role doesn't re-hit the API. { fresh: true }
  // skips the cache (Settings → Feature flags reads the team's record layout).
  whoami: (opts) => apiFetch('/api/auth/whoami', { cacheMs: opts && opts.fresh ? 0 : 60_000 }),
  // capability registry + what the caller may do (Settings → Permissions)
  authPermissions: () => apiFetch('/api/auth/permissions', { cacheMs: 60_000 }),

  // dashboard
  dashboardCounts: (p = {}) => apiFetch(`/api/dashboard/counts${qs(withScope(p))}`),
  // Per-stage {count, est_fees} over ALL active engagements — replaces the
  // home page's 2MB limit=1000 pull (which also silently truncated the chart
  // to the 1,000 most-recently-staged rows).
  dashboardPipeline: (p = {}) => apiFetch(`/api/dashboard/pipeline${qs(withScope({ my_team: true, ...p }))}`),
  // The signed-in user's automatic follow-up reminders (new Lost Deals
  // findings: unanswered inbound / gone-quiet threads).
  dashboardReminders: () => apiFetch('/api/dashboard/reminders'),

  // analytics (AI dashboard builder). Bodies are plain objects — apiFetch
  // auto-serializes them. generate is a slow (LLM) POST; never cached.
  analytics: {
    // Bodies are JSON.stringify'd here (not left as objects) so the in-browser
    // mock router — which JSON.parses opts.body — works too; apiFetch passes a
    // string through untouched for the live path.
    generate:        (body)              => apiFetch('/api/analytics/generate', { method: 'POST', body: JSON.stringify(body) }),
    // The list builder checks the data before it answers, streaming each step:
    // onStatus("Checking how states are stored…") until the table lands.
    buildListStream: (body, onStatus)    => postEventStream('/api/analytics/list/stream', body, onStatus),
    run:             (body)              => apiFetch('/api/analytics/run', { method: 'POST', body: JSON.stringify(body) }),
    history:         (limit)             => apiFetch(`/api/analytics/history?limit=${limit || 20}`),
    feedback:        (id, body)          => apiFetch(`/api/analytics/history/${id}/feedback`, { method: 'POST', body: JSON.stringify(body) }),
    sources:         ()                  => apiFetch('/api/analytics/sources', { cacheMs: 60_000 }),
    values:          (source, column)    => apiFetch(`/api/analytics/values?source=${encodeURIComponent(source)}&column=${encodeURIComponent(column)}`, { cacheMs: 60_000 }),
    suggest:         (opts)              => { const o = opts || {}; const q = [o.refresh && 'refresh=true', o.ai && 'ai=true'].filter(Boolean).join('&'); return apiFetch('/api/analytics/suggest' + (q ? '?' + q : ''), { cacheMs: o.refresh ? 0 : 300_000 }); },
    overview:        ()                  => apiFetch('/api/analytics/overview', { cacheMs: 120_000 }),
    build:           (model)             => apiFetch('/api/analytics/build', { method: 'POST', body: JSON.stringify(model) }),
    drill:           (body)              => apiFetch('/api/analytics/drill', { method: 'POST', body: JSON.stringify(body) }),
    detail:          (body)              => apiFetch('/api/analytics/detail', { method: 'POST', body: JSON.stringify(body) }),
    listDashboards:  ()                  => apiFetch('/api/analytics/dashboards'),
    getDashboard:    (id)                => apiFetch(`/api/analytics/dashboards/${id}`),
    createDashboard: (data)              => apiFetch('/api/analytics/dashboards', { method: 'POST', body: JSON.stringify(data) }),
    updateDashboard: (id, data)          => apiFetch(`/api/analytics/dashboards/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteDashboard: (id)                => apiFetch(`/api/analytics/dashboards/${id}`, { method: 'DELETE' }),
    shareDashboard:  (id, data)          => apiFetch(`/api/analytics/dashboards/${id}/share`, { method: 'PUT', body: JSON.stringify(data) }),
    renameGroup:     (data)              => apiFetch('/api/analytics/groups/rename', { method: 'POST', body: JSON.stringify(data) }),
    createWidget:    (dashId, data)      => apiFetch(`/api/analytics/dashboards/${dashId}/widgets`, { method: 'POST', body: JSON.stringify(data) }),
    updateWidget:    (dashId, wid, data) => apiFetch(`/api/analytics/dashboards/${dashId}/widgets/${wid}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteWidget:    (dashId, wid)       => apiFetch(`/api/analytics/dashboards/${dashId}/widgets/${wid}`, { method: 'DELETE' }),
    runWidget:       (dashId, wid, refresh, filters, offset) => apiFetch(`/api/analytics/dashboards/${dashId}/widgets/${wid}/run${refresh ? '?refresh=true' : ''}`, { method: 'POST', body: JSON.stringify({ filters: filters || [], offset: offset || 0 }) }),
    // Server-side CSV downloads (fetch → blob; resolve to {truncated}).
    exportCsv:       (body)              => exportFetch('/api/analytics/export', body),
    exportWidgetCsv: (dashId, wid, body) => exportFetch(`/api/analytics/dashboards/${dashId}/widgets/${wid}/export`, body),
    widgetDetail:    (dashId, wid, body) => apiFetch(`/api/analytics/dashboards/${dashId}/widgets/${wid}/detail`, { method: 'POST', body: JSON.stringify(body || {}) }),
  },

  // Saved Views — shareable saved (source/columns/filters/sort) tables (VIEWS_DESIGN.md).
  views: {
    list:     ()             => apiFetch('/api/views'),
    get:      (id)           => apiFetch(`/api/views/${id}`),
    create:   (data)         => apiFetch('/api/views', { method: 'POST', body: JSON.stringify(data) }),
    update:   (id, data)     => apiFetch(`/api/views/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    remove:   (id)           => apiFetch(`/api/views/${id}`, { method: 'DELETE' }),
    setShares:(id, data)     => apiFetch(`/api/views/${id}/shares`, { method: 'PUT', body: JSON.stringify(data) }),
    renameGroup: (data)      => apiFetch('/api/views/groups/rename', { method: 'POST', body: JSON.stringify(data) }),
    duplicate:(id)           => apiFetch(`/api/views/${id}/duplicate`, { method: 'POST' }),
    run:      (id, refresh, filters) => apiFetch(`/api/views/${id}/run${refresh ? '?refresh=true' : ''}`, { method: 'POST', body: JSON.stringify({ filters: filters || [] }) }),
    exportCsv:(id, body)     => exportFetch(`/api/views/${id}/export`, body),
    preview:  (model)        => apiFetch('/api/views/preview', { method: 'POST', body: JSON.stringify(model) }),
    generate: (body)         => apiFetch('/api/views/generate', { method: 'POST', body: JSON.stringify(body) }),
    sources:  ()             => apiFetch('/api/views/sources', { cacheMs: 60_000 }),
    values:   (source, column) => apiFetch(`/api/views/values?source=${encodeURIComponent(source)}&column=${encodeURIComponent(column)}`, { cacheMs: 60_000 }),
    drill:    (body)         => apiFetch('/api/views/drill', { method: 'POST', body: JSON.stringify(body) }),
    detail:   (body)         => apiFetch('/api/views/detail', { method: 'POST', body: JSON.stringify(body) }),
  },

  // Ask Atlas — conversational assistant behind the ⌘K search bar. chat is a
  // slow (LLM tool-loop) POST; never cached.
  assistant: {
    chat:          (body) => apiFetch('/api/assistant/chat', { method: 'POST', body: JSON.stringify(body) }),
    // chatStream(body, onStatus) — live progress via SSE, same resolved payload as chat.
    chatStream:    assistantChatStream,
    // record = {entity_type, id}: only the threads opened from that page.
    conversations: (record) => apiFetch(
      '/api/assistant/conversations' + (record
        ? `?entity_type=${encodeURIComponent(record.entity_type)}&entity_id=${encodeURIComponent(record.id)}`
        : ''),
      { cacheMs: 0 },
    ),
    conversation:  (id)   => apiFetch(`/api/assistant/conversations/${id}`, { cacheMs: 0 }),
    remove:        (id)   => apiFetch(`/api/assistant/conversations/${id}`, { method: 'DELETE' }),
    // Share by link (#/ask/<id>): share() stamps the owner's thread readable by
    // the team; shared() reads one (404 unless shared); continueShared() copies
    // it into a new thread of the caller's own.
    share:         (id)   => apiFetch(`/api/assistant/conversations/${id}/share`, { method: 'POST' }),
    unshare:       (id)   => apiFetch(`/api/assistant/conversations/${id}/share`, { method: 'DELETE' }),
    shared:        (id)   => apiFetch(`/api/assistant/shared/${id}`, { cacheMs: 0 }),
    continueShared: (id)  => apiFetch(`/api/assistant/shared/${id}/continue`, { method: 'POST' }),
    preferences:   ()     => apiFetch('/api/assistant/preferences'),
    removePreference: (id) => apiFetch(`/api/assistant/preferences/${id}`, { method: 'DELETE' }),
  },

  // Per-user UI layout — column order/visibility/width, view mode (105).
  // list() returns every stored view for the signed-in user in one call, so
  // the whole app pays a single request on boot. cacheMs 0: a save must be
  // visible to the next reader immediately, and the payload is tiny.
  uiPrefs: {
    list:   ()               => apiFetch('/api/ui-prefs', { cacheMs: 0 }),
    save:   (viewKey, prefs) => apiFetch(`/api/ui-prefs/${encodeURIComponent(viewKey)}`,
                                  { method: 'PUT', body: JSON.stringify({ prefs }) }),
    reset:  (viewKey)        => apiFetch(`/api/ui-prefs/${encodeURIComponent(viewKey)}`,
                                  { method: 'DELETE' }),
  },

  // Phone landing counter (mobile/shell.jsx BestOnDesktop): which desktop
  // pages get opened on a phone. Fire and forget — never throws, never shows.
  phoneLanding: (view, action, device) => {
    if (window.AtlasMockApi && window.AtlasMockApi.forceMock) return Promise.resolve(null);
    return apiFetch('/api/ui-events/phone-landing', {
      method: 'POST', body: JSON.stringify({ view, action, device }), timeoutMs: 15000,
    }).catch(() => null);
  },
  // Record-page counter (routes/ui_events.py record-view): one per record
  // open — { page, variant, device, layout, panel, action, via }, never a
  // record id. Fire and forget like phoneLanding; callers skip deal guests.
  recordView: (event) => {
    if (window.AtlasMockApi && window.AtlasMockApi.forceMock) return Promise.resolve(null);
    return apiFetch('/api/ui-events/record-view', {
      method: 'POST', body: JSON.stringify(event), timeoutMs: 15000,
    }).catch(() => null);
  },

  // companies
  listCompanies: (p = {}) => apiFetch(`/api/companies${qs(withScope({ page: 1, limit: 200, ...p }))}`),
  getCompany: (id) => apiFetch(`/api/companies/${id}`),
  createCompany: (data) => apiFetch('/api/companies', { method: 'POST', body: JSON.stringify(data) }),
  // Create-time duplicate guards — likely-existing records for typed identity fields
  checkCompanyDuplicates: (p = {}) => apiFetch(`/api/companies/duplicate-check${qs(p)}`),
  checkContactDuplicates: (p = {}) => apiFetch(`/api/contacts/duplicate-check${qs(p)}`),
  // Live deals already open at a company — the New deal drawer's warning.
  checkEngagementDuplicates: (p = {}) => apiFetch(`/api/engagements/duplicate-check${qs(p)}`, { cacheMs: 0 }),
  // Fold another deal at the same company into this one (app/deal_merge.py).
  mergeEngagements: (keepId, mergeId) => apiFetch(`/api/engagements/${keepId}/merge`, { method: 'POST', body: JSON.stringify({ merge_id: mergeId }) }),
  // Identity-key registry (read-only) — Settings → Objects "Identity" panel
  identityKeys: () => apiFetch('/api/identity/keys'),

  // dedup review queue (Needs cleaning → Duplicates tab)
  dedupCandidates: (p = {}) => apiFetch(`/api/dedup/companies${qs(p)}`),
  // Duplicates queue for companies / people / deals (routes/dedup_queue.py).
  dedupQueue:        (p = {}) => apiFetch(`/api/dedup/queue${qs(p)}`, { cacheMs: 0 }),
  dedupQueueCounts:  () => apiFetch('/api/dedup/queue/counts', { cacheMs: 0 }),
  dedupQueueMerge:   (id, keepId) => apiFetch(`/api/dedup/queue/${id}/merge`, { method: 'POST', body: JSON.stringify({ keep_id: keepId }) }),
  dedupQueueDismiss: (id) => apiFetch(`/api/dedup/queue/${id}/dismiss`, { method: 'POST' }),
  dedupMergeStrong:  (ids) => apiFetch('/api/dedup/queue/merge-strong', { method: 'POST', body: JSON.stringify({ ids }), timeoutMs: 300_000 }),
  dedupScan:         () => apiFetch('/api/dedup/queue/scan', { method: 'POST' }),
  dedupScanStatus:   () => apiFetch('/api/dedup/queue/scan', { cacheMs: 0 }),
  dedupMerge:   (keep_id, merge_id) => apiFetch('/api/dedup/companies/merge',
                                        { method: 'POST', body: JSON.stringify({ keep_id, merge_id }) }),
  dedupDismiss: (company_a_id, company_b_id) => apiFetch('/api/dedup/companies/dismiss',
                                        { method: 'POST', body: JSON.stringify({ company_a_id, company_b_id }) }),
  // SourceScrub enrichment (Data Connect API; no-ops when not configured)
  companyEnrichStatus: (id) => apiFetch(`/api/companies/${id}/enrich`),
  enrichCompany: (id) => apiFetch(`/api/companies/${id}/enrich`, { method: 'POST' }),
  // One call for a record page's cards (routes/record_summary.py): kind is
  // deal | company | contact. Never cached — the page refetches after writes.
  recordSummary: (kind, id) => apiFetch(`/api/records/${kind}/${id}/summary`, { cacheMs: 0 }),

  // contacts
  listContacts: (p = {}) => apiFetch(`/api/contacts${qs(withScope({ page: 1, limit: 200, ...p }))}`),
  getContact:   (id) => apiFetch(`/api/contacts/${id}`),
  createContact: (data) => apiFetch('/api/contacts', { method: 'POST', body: JSON.stringify(data) }),

  // referrals (enriched grid feed — one flat row per referred contact)
  listReferrals: (p = {}) => apiFetch(`/api/referrals${qs(p)}`),

  // activities (interaction log)
  listActivities:  (p = {}) => apiFetch(`/api/activities${qs({ page: 1, limit: 50, ...p })}`),
  createActivity:  (data) => apiFetch('/api/activities', { method: 'POST', body: JSON.stringify(data) }),
  updateActivity:  (id, data) => apiFetch(`/api/activities/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteActivity:  (id) => apiFetch(`/api/activities/${id}`, { method: 'DELETE' }),

  // audit log — global Data-audit page + per-record history (object_type+object_id)
  audit: (p = {}) => apiFetch(`/api/audit${qs({ limit: 100, ...p })}`),
  auditFacets: (p = {}) => apiFetch(`/api/audit/facets${qs(p)}`),

  // Territory map — a dealmaker's book of companies aggregated by HQ state
  territoryStates: (p = {}) => apiFetch(`/api/territory/states${qs(p)}`),
  territoryCompanies: (p = {}) => apiFetch(`/api/territory/companies${qs(p)}`),
  territoryNearby: (p = {}) => apiFetch(`/api/territory/nearby${qs(p)}`),
  territoryCities: (p = {}) => apiFetch(`/api/territory/cities${qs(p)}`),
  territoryGeocode: (p = {}) => apiFetch(`/api/territory/geocode${qs(p)}`),
  // AI address enrichment jobs (fills hq_street/lat/lng + no-state gaps)
  geoEnrichStart: (body) => apiFetch('/api/geo-enrichment/jobs', { method: 'POST', body: JSON.stringify(body) }),
  geoEnrichJobs: () => apiFetch('/api/geo-enrichment/jobs'),
  geoEnrichJob: (id) => apiFetch(`/api/geo-enrichment/jobs/${id}`),
  geoEnrichCancel: (id) => apiFetch(`/api/geo-enrichment/jobs/${id}/cancel`, { method: 'POST' }),

  // Team Pulse — admin-only weekly dealmaker activity (view_team_activity)
  teamPulse: (p = {}) => apiFetch(`/api/team/activity/weekly${qs(p)}`),
  teamPulseBriefs: () => apiFetch('/api/team/activity/brief'),
  teamPulseDetail: (p) => apiFetch(`/api/team/activity/detail${qs(p)}`),
  offboardingReport: (memberId) => apiFetch(`/api/team/members/${memberId}/offboarding-report`),
  // Dealmaker daily — admin-only per-dealmaker daily reports (view_team_activity;
  // config + send need manage_members). Reads are uncached: a Refresh must show.
  dailyReports: (date) => apiFetch(`/api/team/daily-reports${qs({ date })}`, { cacheMs: 0 }),
  dailyReportDates: () => apiFetch('/api/team/daily-reports/dates', { cacheMs: 0 }),
  dailyReportsGenerate: (date, memberId) => apiFetch(`/api/team/daily-reports/generate${qs({ date, member_id: memberId })}`, { method: 'POST' }),
  dailyReportsConfig: () => apiFetch('/api/team/daily-reports/config', { cacheMs: 0 }),
  dailyReportsSaveConfig: (cfg) => apiFetch('/api/team/daily-reports/config', { method: 'PUT', body: JSON.stringify(cfg) }),
  dailyReportsSend: (date, toMe) => apiFetch(`/api/team/daily-reports/send${qs({ date, to_me: toMe ? 'true' : undefined })}`, { method: 'POST' }),
  downloadDailyReportHtml: (date) =>
    exportFetch(`/api/team/daily-reports/export.html${qs({ date })}`, null, 'GET', `dealmaker-daily-${date}.html`),
  downloadBookHealthHtml: (date) =>
    exportFetch(`/api/team/daily-reports/book-health.html${qs({ date })}`, null, 'GET', `book-health-${date}.html`),
  // The same two emails as HTML text for the phone reader. No date = the
  // server's latest report day (the filename in the result names it).
  dailyReportHtml: (date) => fetchHtmlDoc(`/api/team/daily-reports/export.html${qs({ date })}`),
  bookHealthHtml: (date) => fetchHtmlDoc(`/api/team/daily-reports/book-health.html${qs({ date })}`),
  dailyReportsCleanup: (since) => apiFetch(`/api/team/daily-reports/cleanup${qs({ since })}`, { cacheMs: 0 }),
  dailyReportsCleanupDetail: (p) => apiFetch(`/api/team/daily-reports/cleanup/detail${qs(p)}`, { cacheMs: 0 }),
  // Self-contained HTML export of the same report; fetch → blob so the
  // Authorization header rides along (filename comes from Content-Disposition).
  downloadOffboardingReport: (memberId) =>
    exportFetch(`/api/team/members/${memberId}/offboarding-report.html`, null, 'GET', 'offboarding-report.html'),

  // integrations — per-user credentials for third-party services
  integrationCatalog:      ()              => apiFetch('/api/integrations/catalog'),
  listIntegrations:        ()              => apiFetch('/api/integrations'),
  connectFireflies:        (api_key, features) => apiFetch('/api/integrations/fireflies/connect',
                                                       { method: 'POST', body: { api_key, features } }),
  updateIntegrationFeatures: (provider, features) => apiFetch(
    `/api/integrations/${provider}/features`, { method: 'PATCH', body: { features } }),
  // Slack — one workspace-level bot for the tasks daily brief.
  slackStatus:     () => apiFetch('/api/integrations/slack/status'),
  // The signed-in member's own Slack settings: feed (full | action_only)
  // and where approval cards go (channel | dm).
  slackPrefs:      () => apiFetch('/api/integrations/slack/prefs'),
  setSlackPrefs:   (patch) => apiFetch('/api/integrations/slack/prefs', { method: 'PUT', body: JSON.stringify(patch) }),
  slackConnect:    (bot_token) => apiFetch('/api/integrations/slack/connect', { method: 'POST', body: JSON.stringify({ bot_token }) }),
  slackTest:       () => apiFetch('/api/integrations/slack/test', { method: 'POST' }),
  slackDisconnect: () => apiFetch('/api/integrations/slack/disconnect', { method: 'POST' }),
  // Firecrawl monitors ("Signals") — admin registry behind Settings → Integrations.
  firecrawlStatus:         () => apiFetch('/api/integrations/firecrawl/status'),
  firecrawlMonitors:       (includeDeleted = false) => apiFetch(`/api/integrations/firecrawl/monitors?include_deleted=${includeDeleted ? 'true' : 'false'}`),
  firecrawlSyncMonitors:   () => apiFetch('/api/integrations/firecrawl/monitors/sync', { method: 'POST' }),
  firecrawlMonitorAction:  (monitorId, action) => apiFetch(`/api/integrations/firecrawl/monitors/${encodeURIComponent(monitorId)}/${action}`, { method: 'POST' }),
  firecrawlDeleteMonitor:  (monitorId) => apiFetch(`/api/integrations/firecrawl/monitors/${encodeURIComponent(monitorId)}`, { method: 'DELETE' }),
  firecrawlEvents:         (params = {}) => apiFetch(`/api/integrations/firecrawl/events?${new URLSearchParams(params).toString()}`),
  firecrawlProcessEvents:  () => apiFetch('/api/integrations/firecrawl/events/process', { method: 'POST' }),
  // Dialer (PhoneBurner) — the Calling page (calling team only) and the
  // contact/company Calls panels (everyone). See backend app/dialer.py.
  // What each team role sees (Settings → Permissions). PUT is admin-only.
  rolePages:        () => apiFetch('/api/auth/role-pages'),
  setRolePages:     (role, pages) => apiFetch('/api/auth/role-pages', { method: 'PUT', body: JSON.stringify({ role, pages }) }),
  // The team's record layout ('v2' | 'classic'; whoami.record_layout). Admin only (Settings → Feature flags).
  setRecordLayout:  (layout) => apiFetch('/api/auth/record-layout', { method: 'PUT', body: JSON.stringify({ layout }) }),
  // Team-wide feature flags (app/feature_flags.py). Admin only (Settings → Feature flags).
  featureFlags:     () => apiFetch('/api/auth/feature-flags', { cacheMs: 0 }),
  setFeatureFlag:   (key, on) => apiFetch(`/api/auth/feature-flags/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify({ on }) }),
  // Calling data is live and polled: never serve it from the 30 s GET cache
  // (a cached "running" batch froze the send progress line, 2026-09-29).
  dialerStatus:     () => apiFetch('/api/dialer/status', { cacheMs: 0 }),
  dialerConnect:    (token) => apiFetch('/api/dialer/connect', { method: 'POST', body: JSON.stringify({ token }) }),
  dialerDisconnect: () => apiFetch('/api/dialer/disconnect', { method: 'POST' }),
  dialerSync:       (days = 2) => apiFetch(`/api/dialer/sync?days=${days}`, { method: 'POST' }),
  dialerEvents:     (limit = 50) => apiFetch(`/api/dialer/events?limit=${limit}`, { cacheMs: 0 }),
  dialerCalls:      (params = {}) => apiFetch(`/api/dialer/calls?${new URLSearchParams(params).toString()}`, { cacheMs: 0 }),
  dialerCandidates: (filters = {}) => apiFetch('/api/dialer/candidates', { method: 'POST', body: JSON.stringify(filters) }),
  dialerSend:       (body) => apiFetch('/api/dialer/send', { method: 'POST', body: JSON.stringify(body) }),
  dialerBatches:    () => apiFetch('/api/dialer/batches', { cacheMs: 0 }),
  // The account's PhoneBurner tags (the Send tab's tag picker).
  dialerTags:       () => apiFetch('/api/dialer/tags', { cacheMs: 0 }),
  // Calling → Email campaigns: SuperSend pushes split by mail filter (app/dialer_campaigns.py).
  dialerEmailCampaigns: () => apiFetch('/api/dialer/email/campaigns', { cacheMs: 0 }),
  // State / city pickers on the Calling lists (company counts).
  dialerPlaces:         (states = []) => apiFetch(`/api/dialer/places?${new URLSearchParams(states.map((x) => ['states', x])).toString()}`),
  dialerEmailPlan:      (body) => apiFetch('/api/dialer/email/plan', { method: 'POST', body: JSON.stringify(body) }),
  dialerEmailPush:      (body) => apiFetch('/api/dialer/email/push', { method: 'POST', body: JSON.stringify(body) }),
  dialerEmailCampaign:  (id, body) => apiFetch(`/api/dialer/email/campaigns/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // Who a campaign emailed and when each goes to PhoneBurner (or why not).
  dialerEmailUpNext:    (id) => apiFetch(`/api/dialer/email/campaigns/${encodeURIComponent(id)}/up-next`, { cacheMs: 0 }),
  dialerEmailSettings:  (body) => apiFetch('/api/dialer/email/settings', { method: 'PUT', body: JSON.stringify(body) }),
  dialerEmailOpeners:   (body) => apiFetch('/api/dialer/email/openers', { method: 'POST', body: JSON.stringify(body) }),
  dialerEmailHeld:      (id) => apiFetch(`/api/dialer/email/held?campaign_id=${encodeURIComponent(id)}`, { cacheMs: 0 }),
  dialerEmailFields:    (body) => apiFetch('/api/dialer/email/fields', { method: 'POST', body: JSON.stringify(body) }),
  dialerEmailCopyRules: () => apiFetch('/api/dialer/email/copy-rules', { cacheMs: 0 }),
  dialerEmailSaveCopyRules: (body) => apiFetch('/api/dialer/email/copy-rules', { method: 'PUT', body: JSON.stringify(body) }),
  // The call queue ("Mark to call"): mark/unmark, and import a call list.
  dialerQueue:      (body) => apiFetch('/api/dialer/queue', { method: 'POST', body: JSON.stringify(body) }),
  dialerQueueLists: () => apiFetch('/api/dialer/queue/lists', { cacheMs: 0 }),
  dialerImportList: async (file) => {
    // Multipart — apiFetch would force a JSON content type.
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers = { Authorization: `Bearer ${getToken()}` };
    const activeUser = localStorage.getItem('atlas.activeUserId');
    if (activeUser) headers['X-Active-User'] = activeUser;
    const res = await fetch(`${getBaseUrl()}/api/dialer/queue/import`, { method: 'POST', headers, body: fd });
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try { msg = (await res.json()).detail || msg; } catch (_) {}
      throw new Error(msg);
    }
    return res.json();
  },
  dialerCreateListContacts: (body) => apiFetch('/api/dialer/queue/create-contacts', { method: 'POST', body: JSON.stringify(body) }),
  // Uploaded lists for Calling → Email campaigns (app/contact_lists.py).
  dialerEmailLists:     () => apiFetch('/api/dialer/email/lists', { cacheMs: 0 }),
  dialerEmailListAdd:   (id, body) => apiFetch(`/api/dialer/email/lists/${encodeURIComponent(id)}/add`, { method: 'POST', body: JSON.stringify(body) }),
  dialerEmailListImport: async (file) => {
    // Multipart — apiFetch would force a JSON content type.
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers = { Authorization: `Bearer ${getToken()}` };
    const activeUser = localStorage.getItem('atlas.activeUserId');
    if (activeUser) headers['X-Active-User'] = activeUser;
    const res = await fetch(`${getBaseUrl()}/api/dialer/email/lists/import`, { method: 'POST', headers, body: fd });
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try { msg = (await res.json()).detail || msg; } catch (_) {}
      throw new Error(msg);
    }
    return res.json();
  },
  dialerRouting:    (body) => apiFetch('/api/dialer/routing', { method: 'PUT', body: JSON.stringify(body) }),
  // PhoneBurner custom fields ↔ Atlas values (app/dialer_fields.py).
  dialerCustomFields:     () => apiFetch('/api/dialer/custom-fields', { cacheMs: 0 }),
  dialerSaveCustomFields: (body) => apiFetch('/api/dialer/custom-fields', { method: 'PUT', body: JSON.stringify(body) }),
  dialerNewCustomField:   (body) => apiFetch('/api/dialer/custom-fields/new', { method: 'POST', body: JSON.stringify(body) }),
  dialerDeleteCustomField: (id) => apiFetch(`/api/dialer/custom-fields/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  // Seller briefs + comps for the queue (what the caller's notes carry).
  dialerBriefsRun:  (body = {}) => apiFetch('/api/dialer/briefs/run', { method: 'POST', body: JSON.stringify(body) }),
  dialerRestart: (contactIds) => apiFetch('/api/dialer/contacts/restart', { method: 'POST', body: JSON.stringify({ contact_ids: contactIds }) }),
  dialerPrepEstimate: (contactIds) => apiFetch('/api/dialer/prep/estimate', { method: 'POST', body: JSON.stringify({ contact_ids: contactIds }) }),
  // The caller's PhoneBurner note: template + live preview (app/dialer_note.py).
  dialerNoteTemplate:     () => apiFetch('/api/dialer/note-template', { cacheMs: 0 }),
  dialerSaveNoteTemplate: (body) => apiFetch('/api/dialer/note-template', { method: 'PUT', body: JSON.stringify(body) }),
  dialerNotePreview:      (body) => apiFetch('/api/dialer/note-preview', { method: 'POST', body: JSON.stringify(body) }),
  dialerSent:       (params = {}) => apiFetch(`/api/dialer/sent?${new URLSearchParams(params).toString()}`, { cacheMs: 0 }),
  dialerBatch:      (id) => apiFetch(`/api/dialer/batches/${id}`, { cacheMs: 0 }),
  dialerBriefsStatus:    () => apiFetch('/api/dialer/briefs/status', { cacheMs: 0 }),
  dialerRoutingFolders:  () => apiFetch('/api/dialer/routing-folders', { cacheMs: 0 }),
  dialerDashboard:  (params = {}) => apiFetch(`/api/dialer/dashboard?${new URLSearchParams(params).toString()}`, { cacheMs: 0 }),
  dialerSeatToken:  (body) => apiFetch('/api/dialer/seat-token', { method: 'POST', body: JSON.stringify(body) }),
  dialerCheckTranscripts: () => apiFetch('/api/dialer/transcripts/check', { method: 'POST' }),
  dialerFolderSync: () => apiFetch('/api/dialer/folder-sync', { cacheMs: 0 }),
  dialerScoreCall: (id) => apiFetch(`/api/dialer/calls/${id}/score`, { method: 'POST' }),
  dialerCall: (id) => apiFetch(`/api/dialer/calls/${id}`, { cacheMs: 0 }),
  dialerActions: (params = {}) => apiFetch(`/api/dialer/actions?${new URLSearchParams(params).toString()}`, { cacheMs: 0 }),
  dialerApplyAction: (id, edits) => apiFetch(`/api/dialer/actions/${id}/apply`, { method: 'POST', body: JSON.stringify({ edits: edits || {} }) }),
  dialerDismissAction: (id) => apiFetch(`/api/dialer/actions/${id}/dismiss`, { method: 'POST' }),
  dialerEmails: (status) => apiFetch(`/api/dialer/emails${status ? `?status=${encodeURIComponent(status)}` : ''}`, { cacheMs: 0 }),
  dialerEmailDraft: (callId) => apiFetch('/api/dialer/emails', { method: 'POST', body: JSON.stringify({ call_id: callId }) }),
  dialerEmailEdit: (id, edits) => apiFetch(`/api/dialer/emails/${id}`, { method: 'PATCH', body: JSON.stringify(edits || {}) }),
  dialerEmailSend: (id, edits) => apiFetch(`/api/dialer/emails/${id}/send`, { method: 'POST', body: JSON.stringify(edits || {}) }),
  dialerEmailDismiss: (id) => apiFetch(`/api/dialer/emails/${id}/dismiss`, { method: 'POST' }),
  dialerEmailWrite: (id) => apiFetch(`/api/dialer/emails/${id}/write`, { method: 'POST' }),
  dialerEmailTemplates: () => apiFetch('/api/dialer/email-templates', { cacheMs: 0 }),
  dialerSaveEmailTemplates: (templates) => apiFetch('/api/dialer/email-templates', { method: 'PUT', body: JSON.stringify({ templates }) }),
  dialerRunFolderSync: () => apiFetch('/api/dialer/folder-sync/run', { method: 'POST' }),
  dialerSetFolderSync: (apply) => apiFetch('/api/dialer/folder-sync', { method: 'PUT', body: JSON.stringify({ apply }) }),
  dialerCallbackEvents: () => apiFetch('/api/dialer/callback-events', { cacheMs: 0 }),
  dialerRunCallbackEvents: () => apiFetch('/api/dialer/callback-events/run', { method: 'POST' }),
  dialerSetCallbackEvents: (on) => apiFetch('/api/dialer/callback-events', { method: 'PUT', body: JSON.stringify({ on }) }),
  dialerDeleteSeatToken: (id) => apiFetch(`/api/dialer/seat-token/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  dialerSlackChannels:     () => apiFetch('/api/dialer/slack-channels', { cacheMs: 0 }),
  dialerSaveSlackChannels: (body) => apiFetch('/api/dialer/slack-channels', { method: 'PUT', body: JSON.stringify(body) }),
  contactCalls:     (contactId) => apiFetch(`/api/dialer/contacts/${contactId}/calls`, { cacheMs: 0 }),
  companyCalls:     (companyId) => apiFetch(`/api/dialer/companies/${companyId}/calls`),
  // Web signals (Signals page) — what Firecrawl found on the open web.
  signals:        (params = {}) => apiFetch(`/api/signals?${new URLSearchParams(params).toString()}`),
  signalsSummary: () => apiFetch('/api/signals/summary'),
  signalAct:      (id, verb, body = {}) => apiFetch(`/api/signals/${id}/${verb}`, { method: 'POST', body: JSON.stringify(body) }),
  // Signals → Prospecting (app/signal_prospecting.py): one row, or every
  // eligible row the Web tab has loaded. Both start the pipeline run.
  signalProspect:      (id) => apiFetch(`/api/signals/${id}/prospect`, { method: 'POST' }),
  signalsProspectMany: (ids) => apiFetch('/api/signals/prospect', { method: 'POST', body: JSON.stringify({ signal_ids: ids }) }),
  // Market view (app/market_view.py): what sold in our trades, who bought,
  // and an acquirer → Buyers › Candidates. `months: 0` = everything.
  marketSummary:       (p = {}) => apiFetch(`/api/market/summary${qs(p)}`),
  marketTransactions:  (p = {}) => apiFetch(`/api/market/transactions${qs(p)}`, { cacheMs: 0 }),
  marketAddBuyerCandidate: (data) => apiFetch('/api/market/acquirers/candidate', { method: 'POST', body: JSON.stringify(data) }),
  slackMirror:     (email) => apiFetch('/api/integrations/slack/mirror', { method: 'PUT', body: JSON.stringify({ email }) }),
  // Organization-granted mailbox access (admin): the org opened mailboxes to
  // Atlas once; these connect a member's mailbox without them signing in.
  // Calendly — Growth Partner call intake (Settings → Integrations → Calendly card).
  calendlyStatus:          ()              => apiFetch('/api/integrations/calendly/status'),
  calendlyEventTypes:      ()              => apiFetch('/api/integrations/calendly/event-types'),
  calendlySaveSettings:    (body)          => apiFetch('/api/integrations/calendly/settings', { method: 'PUT', body: JSON.stringify(body) }),
  calendlyImportRecent:    ()              => apiFetch('/api/integrations/calendly/import', { method: 'POST' }),
  calendlySlackChannels:   ()              => apiFetch('/api/integrations/calendly/slack-channels'),
  calendlyRegisterWebhook: ()              => apiFetch('/api/integrations/calendly/webhook/register', { method: 'POST' }),
  // Website visitors (RB2B + website forms) — routes/web_visitors.py
  webTrackingSettings:     ()              => apiFetch('/api/web-tracking/settings', { cacheMs: 0 }),
  webTrackingRotate:       (source)        => apiFetch(`/api/web-tracking/settings/rotate/${source}`, { method: 'POST' }),
  webVisitAlerts:          ()              => apiFetch('/api/web-tracking/alerts', { cacheMs: 0 }),
  webVisitAlertsSave:      (body)          => apiFetch('/api/web-tracking/alerts', { method: 'PUT', body: JSON.stringify(body) }),
  importRb2bCsv:           async (file, dryRun) => {
    // Multipart — apiFetch would force a JSON content type.
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers = { Authorization: `Bearer ${getToken()}` };
    const activeUser = localStorage.getItem('atlas.activeUserId');
    if (activeUser) headers['X-Active-User'] = activeUser;
    const res = await fetch(`${getBaseUrl()}/api/web-tracking/import/rb2b${dryRun ? '?dry_run=true' : ''}`, { method: 'POST', headers, body: fd });
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try { msg = (await res.json()).detail || msg; } catch (_) {}
      throw new Error(msg);
    }
    return res.json();
  },
  importRb2bState:         ()              => apiFetch('/api/web-tracking/import/rb2b', { cacheMs: 0 }),
  webVisitors:             (params = {})   => apiFetch(`/api/web-tracking/visitors?${new URLSearchParams(params)}`, { cacheMs: 0 }),
  webVisitorVisits:        (id)            => apiFetch(`/api/web-tracking/visitors/${id}/visits`, { cacheMs: 0 }),
  webVisitorAdd:           (id)            => apiFetch(`/api/web-tracking/visitors/${id}/add`, { method: 'POST' }),
  webVisitorDismiss:       (id)            => apiFetch(`/api/web-tracking/visitors/${id}/dismiss`, { method: 'POST' }),
  webVisitorRestore:       (id)            => apiFetch(`/api/web-tracking/visitors/${id}/restore`, { method: 'POST' }),
  recordWebVisits:         (params)        => apiFetch(`/api/web-tracking/visits?${new URLSearchParams(params)}`),
  webSiteStats:            (params = {})   => apiFetch(`/api/web-tracking/site-stats?${new URLSearchParams(params)}`, { cacheMs: 0 }),
  orgMailboxStatus:        ()              => apiFetch('/api/integrations/microsoft/org-access'),
  orgMailboxConnect:       (member_id)     => apiFetch('/api/integrations/microsoft/org-access/connect',
                                                       { method: 'POST', body: { member_id } }),
  orgMailboxConnectAll:    (includeInactive = false) => apiFetch(
    `/api/integrations/microsoft/org-access/connect-all${qs({ include_inactive: includeInactive })}`, { method: 'POST' }),
  disconnectIntegration:   (provider)      => apiFetch(`/api/integrations/${provider}`,
                                                       { method: 'DELETE' }),
  triggerIntegrationSync:  (provider, days = 90) => apiFetch(
    `/api/integrations/${provider}/sync${qs({ days })}`, { method: 'POST' }),
  requestIntegrationBackfill: (credentialId) => apiFetch(
    `/api/integrations/${credentialId}/backfill`, { method: 'POST' }),
  integrationActivity:     (provider, status = 'all') => apiFetch(
    `/api/integrations/${provider}/activity${qs({ status, limit: 500 })}`),
  manuallyLinkNote:        (noteId, { company_id, contact_id }) => apiFetch(
    `/api/integrations/notes/${noteId}/link`,
    { method: 'POST', body: { company_id, contact_id } }),
  // Personal addresses (Gmail/iCloud/…) the active user wants treated as
  // internal — PUT replaces the whole list (the UI always sends it in full).
  getPersonalEmails:       ()              => apiFetch('/api/integrations/personal-emails'),
  setPersonalEmails:       (emails)        => apiFetch('/api/integrations/personal-emails',
                                                       { method: 'PUT', body: { emails } }),

  // background tasks — admin topbar watcher (poller: never cache)
  listBackgroundTasks: () => apiFetch('/api/background-tasks', { cacheMs: 0 }),

  // cleanup queue — companies/engagements where signals contradict stage
  cleanupSummary:     ()             => apiFetch('/api/cleanup/summary'),
  // AI-suggested industries awaiting a human decision (136). A company with
  // no industry is invisible to buyer matching, so this queue is upstream of
  // the Buyers page's Match tab working at all.
  // Contacts on a broadcast list while tagged bounced/unsubscribed/opted-out.
  // Read-only: the fix is a DealCloud tag removal, so Atlas reports rather
  // than acts, and the row clears itself once the tag is gone.
  blastHygiene:       (p = {}) => apiFetch(`/api/cleanup/blast-hygiene${qs({ limit: 300, ...p })}`),
  industryProposals:  (p = {}) => apiFetch(`/api/cleanup/industry-proposals${qs({ status: 'pending', limit: 200, ...p })}`),
  decideIndustryProposal: (id, body) => apiFetch(`/api/cleanup/industry-proposals/${id}`, {
    method: 'POST', body: JSON.stringify(body),
  }),
  // What the classifier actually read (145). Its own call, fetched only when a
  // reviewer expands one row they doubt — the excerpts are a few KB each and
  // the queue loads 300 rows.
  industryProposalEvidence: (id) => apiFetch(`/api/cleanup/industry-proposals/${id}/evidence`),
  cleanupCompanies:   (p = {})       => apiFetch(`/api/cleanup/companies${qs({ limit: 500, ...p })}`),
  cleanupEngagements: (p = {})       => apiFetch(`/api/cleanup/engagements${qs({ limit: 500, ...p })}`),
  // Stale deals — the book-cleanup work surface (bulk hold / exit / next action).
  staleDeals:         ()             => apiFetch('/api/cleanup/stale-deals', { cacheMs: 0 }),
  staleDealsBulk:     (body)         => apiFetch('/api/cleanup/stale-deals/bulk', { method: 'POST', body: JSON.stringify(body) }),
  // Holds — open on-hold seller deals: stale / no active owner (bulk exit / next action).
  // timeoutMs past the server's 30 s statement timeout: a GET that aborts is retried once, re-running the scan.
  staleHolds:         ()             => apiFetch('/api/cleanup/stale-holds', { cacheMs: 0, timeoutMs: 45000 }),
  staleHoldsBulk:     (body)         => apiFetch('/api/cleanup/stale-holds/bulk', { method: 'POST', body: JSON.stringify(body) }),

  // attachments (files mirrored from DealCloud, served via /local-bucket/)
  listAttachments: (p = {}) => apiFetch(`/api/attachments${qs({ page: 1, limit: 200, ...p })}`),
  // Download links are behind the login wall: /download requires a Bearer
  // token and hands back a short-lived signed storage URL. Never cache —
  // the signed URL expires in ~2 minutes, a stale one 400s at Supabase.
  attachmentText: (attachmentId) => apiFetch(`/api/attachments/${attachmentId}/text`),
  attachmentDownloadUrl: async (attachmentId) => {
    const out = await apiFetch(`/api/attachments/${attachmentId}/download?json=1`, { cacheMs: 0 });
    // Prod returns an absolute signed Supabase URL; the dev fallback returns
    // an API-relative /local-bucket path — resolve it against the API origin,
    // not the SPA's.
    if (out && out.url && !/^https?:/i.test(out.url)) out.url = getBaseUrl() + out.url;
    return out;
  },
  // Files the browser can render in a tab. Everything else (Office docs,
  // zips, …) is a download — decided up front from the filename so the UI
  // can label the affordance honestly and no blank tab has to be opened.
  attachmentOpensInline: (filename) => ATTACHMENT_INLINE_RE.test(filename || ''),
  // Click handler for file chips/rows. For viewable files it opens the tab
  // synchronously (popup blockers kill window.open calls that happen after
  // an await), fetches the file bytes, and hands the tab a blob: URL. The
  // blob URL is scoped to this browser session — the address bar never shows
  // anything that works outside it (the signed storage URL would be
  // shareable for its ~2min TTL, and copy-URL-into-incognito is the exact
  // leak that was reported).
  // Non-viewable files used to go through the same blank tab and close it
  // once the download fired — which read as "a tab flashed and nothing
  // happened". They now download in place, with a toast so the user knows
  // to look in Downloads.
  openAttachment: async (attachmentId, filename) => {
    const name = filename || 'attachment';
    const inline = ATTACHMENT_INLINE_RE.test(name);
    const toast = (t) => { if (window.pushToast) window.pushToast(t); };
    // iOS shell: no tabs, no blob: navigations, no <a download>. Viewable
    // files open the signed URL in the in-app Safari sheet (it renders PDFs
    // and images, and only shows the host, not the token); everything else
    // is fetched and handed to the share sheet ("Save to Files", Mail, …).
    const native = window.AtlasNative && window.AtlasNative.isNative ? window.AtlasNative : null;
    if (native) {
      try {
        const { url } = await window.AtlasApi.attachmentDownloadUrl(attachmentId);
        if (inline) { await native.openExternal(url); return; }
        toast({ type: 'info', title: 'Preparing file', body: name });
        let blob = null;
        try {
          const res = await fetch(url);
          if (!res.ok) throw new Error(`${res.status}`);
          blob = await res.blob();
        } catch (_) {
          // Blob fetch failed — let Safari's sheet download it from storage.
          await native.openExternal(`${url}${url.includes('?') ? '&' : '?'}download=${encodeURIComponent(name)}`);
          return;
        }
        await native.saveFile(blob, name, blob.type);
      } catch (e) {
        toast({ type: 'error', title: 'Could not open file', body: (e && e.message) || String(e) });
      }
      return;
    }
    const w = inline ? window.open('about:blank', '_blank') : null;
    try {
      const { url } = await window.AtlasApi.attachmentDownloadUrl(attachmentId);
      if (!inline) {
        toast({ type: 'info', title: 'Downloading', body: name });
        try {
          const res = await fetch(url);
          if (!res.ok) throw new Error(`${res.status}`);
          window.downloadBlob(await res.blob(), name, 'attachment');
        } catch (_) {
          // Blob fetch failed (storage CORS hiccup, huge file) — let the
          // browser download straight from storage. Supabase honours
          // `download=<name>` on signed URLs (Content-Disposition:
          // attachment), so this navigation saves the file without leaving
          // the page.
          window.location.assign(`${url}${url.includes('?') ? '&' : '?'}download=${encodeURIComponent(name)}`);
        }
        return;
      }
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${res.status}`);
        const blob = new Blob([await res.blob()], { type: inlineBlobType(name) });
        const blobUrl = URL.createObjectURL(blob);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
        if (w) { w.location = blobUrl; return; }
      } catch (_) {
        // Blob fetch failed — fall back to opening the short-lived signed
        // URL directly rather than leaving a dead tab.
      }
      if (w) w.location = url;
      else window.location = url;  // popup blocked — same-tab fallback
    } catch (e) {
      if (w) w.close();
      toast({ type: 'error', title: 'Could not open file', body: (e && e.message) || String(e) });
    }
  },
  uploadAttachments: async (noteId, fileList) => {
    const fd = new FormData();
    for (const f of fileList) fd.append('files', f, f.name);
    const headers = { Authorization: `Bearer ${getToken()}` };
    const activeUser = localStorage.getItem('atlas.activeUserId');
    if (activeUser) headers['X-Active-User'] = activeUser;
    const res = await fetch(`${getBaseUrl()}/api/notes/${noteId}/attachments`, {
      method: 'POST', headers, body: fd,
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}: ${await res.text()}`);
    const created = await res.json();
    // Right-rail FilesPanel re-fetches on this — attachments change outside
    // the notes reload path it can't see.
    window.dispatchEvent(new CustomEvent('atlas-attachments-changed'));
    return created;
  },
  detachAttachment: async (noteId, attachmentId) => {
    const out = await apiFetch(`/api/notes/${noteId}/attachments/${attachmentId}`, { method: 'DELETE' });
    window.dispatchEvent(new CustomEvent('atlas-attachments-changed'));
    return out;
  },
  // Files on a team member's profile (routes/team_files.py) — personal to
  // the member and admins; the API 403s everyone else. Downloads use the
  // same openAttachment() path as note files. Never cache the list: an
  // upload or delete must show at once.
  listMemberFiles: (memberId) => apiFetch(`/api/team/members/${memberId}/files`, { cacheMs: 0 }),
  uploadMemberFiles: async (memberId, fileList) => {
    // Raw fetch, like uploadAttachments: apiFetch forces a JSON content-type
    // and multipart needs the browser to write its own boundary.
    const fd = new FormData();
    for (const f of fileList) fd.append('files', f, f.name);
    const headers = { Authorization: `Bearer ${await tokenForRequest()}` };
    const res = await fetch(`${getBaseUrl()}/api/team/members/${memberId}/files`, {
      method: 'POST', headers, body: fd,
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}: ${await res.text()}`);
    return res.json();
  },
  // A company's data vault (routes/company_vault.py) — the CIM working set,
  // one folder per file. Never cache: an upload, move or pull must show at
  // once. pullIntoVault fetches older owner emails from Outlook, so it gets
  // a long timeout; `email.remaining` > 0 means there's more to pull.
  companyVault: (companyId) => apiFetch(`/api/companies/${companyId}/vault`, { cacheMs: 0 }),
  // `ready` uploads straight into the folder's Ready for CIM subfolder.
  uploadVaultFiles: async (companyId, folder, fileList, ready = false) => {
    // Raw fetch, like uploadMemberFiles: multipart needs the browser to
    // write its own boundary.
    const fd = new FormData();
    fd.append('folder', folder);
    if (ready) fd.append('ready', 'true');
    for (const f of fileList) fd.append('files', f, f.name);
    const headers = { Authorization: `Bearer ${await tokenForRequest()}` };
    const res = await fetch(`${getBaseUrl()}/api/companies/${companyId}/vault/files`, {
      method: 'POST', headers, body: fd,
    });
    if (!res.ok) {
      let detail = res.statusText;
      try { detail = (await res.json()).detail || detail; } catch (_) {}
      throw new Error(detail);
    }
    return res.json();
  },
  moveVaultFile: (companyId, attachmentId, folder) =>
    apiFetch(`/api/companies/${companyId}/vault/files/${attachmentId}`, { method: 'PATCH', body: { folder } }),
  // "Parse now": back in line for the CIM worker's reader (never parses in the request).
  parseVaultFile: (companyId, attachmentId) =>
    apiFetch(`/api/companies/${companyId}/vault/files/${attachmentId}/parse`, { method: 'POST' }),
  removeVaultFile: (companyId, attachmentId) =>
    apiFetch(`/api/companies/${companyId}/vault/files/${attachmentId}`, { method: 'DELETE' }),
  pullIntoVault: (companyId) =>
    apiFetch(`/api/companies/${companyId}/vault/pull`, { method: 'POST', timeoutMs: 150_000 }),
  bulkVault: (companyId, body) =>
    apiFetch(`/api/companies/${companyId}/vault/bulk`, { method: 'POST', body }),
  // Many vault files as one .zip (one directory per folder). Raw fetch: the
  // body is a binary stream, not JSON.
  downloadVaultZip: async (companyId, attachmentIds, name) => {
    const headers = { Authorization: `Bearer ${await tokenForRequest()}`, 'Content-Type': 'application/json' };
    const res = await fetch(`${getBaseUrl()}/api/companies/${companyId}/vault/zip`, {
      method: 'POST', headers, body: JSON.stringify({ attachment_ids: attachmentIds, name }),
    });
    if (!res.ok) {
      let detail = res.statusText;
      try { detail = (await res.json()).detail || detail; } catch (_) {}
      throw new Error(detail);
    }
    return window.downloadBlob(await res.blob(), `${name || 'data vault'}.zip`, 'data vault.zip');
  },
  // CIM data room (routes/data_room.py) — the owner request list on top of
  // the vault. Every call returns the whole room, so the view just swaps it in.
  dataRoom: (companyId) => apiFetch(`/api/companies/${companyId}/data-room`, { cacheMs: 0 }),
  startDataRoom: (companyId) =>
    apiFetch(`/api/companies/${companyId}/data-room`, { method: 'POST' }),
  patchDataRoom: (companyId, body) =>
    apiFetch(`/api/companies/${companyId}/data-room`, { method: 'PATCH', body }),
  importDataRoom: (companyId, body) =>
    apiFetch(`/api/companies/${companyId}/data-room/import`, { method: 'POST', body }),
  addDataRoomItem: (companyId, body) =>
    apiFetch(`/api/companies/${companyId}/data-room/items`, { method: 'POST', body }),
  patchDataRoomItem: (companyId, itemId, body) =>
    apiFetch(`/api/companies/${companyId}/data-room/items/${itemId}`, { method: 'PATCH', body }),
  setDataRoomStatus: (companyId, itemId, status, note, markReady = false) =>
    apiFetch(`/api/companies/${companyId}/data-room/items/${itemId}/status`, { method: 'POST', body: { status, note, mark_ready: markReady } }),
  // Drafts (an import, the MCP tool or an architecture run) reach the owner
  // only when someone sends them — explicit ids, never "all".
  sendDataRoomItems: (companyId, itemIds) =>
    apiFetch(`/api/companies/${companyId}/data-room/items/send`, { method: 'POST', body: { item_ids: itemIds } }),
  dismissDataRoomItems: (companyId, itemIds) =>
    apiFetch(`/api/companies/${companyId}/data-room/items/dismiss`, { method: 'POST', body: { item_ids: itemIds } }),
  // Our message on an item's thread (itemId null = the room's own thread).
  postDataRoomMessage: (companyId, itemId, body) =>
    apiFetch(`/api/companies/${companyId}/data-room/messages`, { method: 'POST', body: { item_id: itemId || null, body } }),
  linkDataRoomFile: (companyId, itemId, attachmentId) =>
    apiFetch(`/api/companies/${companyId}/data-room/items/${itemId}/files`, { method: 'POST', body: { attachment_id: attachmentId } }),
  unlinkDataRoomFile: (companyId, itemId, attachmentId) =>
    apiFetch(`/api/companies/${companyId}/data-room/items/${itemId}/files/${attachmentId}`, { method: 'DELETE' }),
  addDataRoomPerson: (companyId, body) =>
    apiFetch(`/api/companies/${companyId}/data-room/people`, { method: 'POST', body }),
  inviteDataRoomPerson: (companyId, personId) =>
    apiFetch(`/api/companies/${companyId}/data-room/people/${personId}/invite`, { method: 'POST' }),
  revokeDataRoomPerson: (companyId, personId) =>
    apiFetch(`/api/companies/${companyId}/data-room/people/${personId}`, { method: 'DELETE' }),
  // CIM Command Center (routes/cim.py). Polled reads are cacheMs: 0.
  cimOverview: (engagementId) => apiFetch(`/api/cim/engagements/${engagementId}`, { cacheMs: 0 }),
  cimInputs: (engagementId) => apiFetch(`/api/cim/engagements/${engagementId}/architecture/inputs`, { cacheMs: 0 }),
  cimRuns: (engagementId) => apiFetch(`/api/cim/engagements/${engagementId}/architecture/runs`, { cacheMs: 0 }),
  startCimRun: (engagementId, body) =>
    apiFetch(`/api/cim/engagements/${engagementId}/architecture/runs`, { method: 'POST', body }),
  cimRun: (runId, { after = 0, eventsOnly = false } = {}) =>
    apiFetch(`/api/cim/architecture/runs/${runId}?after=${after}${eventsOnly ? '&events_only=true' : ''}`, { cacheMs: 0 }),
  cancelCimRun: (runId) => apiFetch(`/api/cim/architecture/runs/${runId}/cancel`, { method: 'POST' }),
  reviewCim: (runId, body) => apiFetch(`/api/cim/architecture/runs/${runId}/reviews`, { method: 'POST', body }),
  cimDiff: (runId, againstId) =>
    apiFetch(`/api/cim/architecture/runs/${runId}/diff?against=${againstId}`, { cacheMs: 0 }),
  askOwnerCim: (runId, questionIds) =>
    apiFetch(`/api/cim/architecture/runs/${runId}/ask`, { method: 'POST', body: { question_ids: questionIds } }),
  // CIM ledger (routes/cim_ledger.py) — the Command Center's Ledger tab. Every
  // read is cacheMs: 0: a review action, a job or another person changes it.
  cimLedger: (eid) => apiFetch(`/api/cim/engagements/${eid}/ledger`, { cacheMs: 0 }),
  cimLedgerGrid: (eid, statement, snapshot) =>
    apiFetch(`/api/cim/engagements/${eid}/ledger/grid?statement=${encodeURIComponent(statement)}${snapshot != null ? `&snapshot=${snapshot}` : ''}`, { cacheMs: 0 }),
  cimLedgerFact: (eid, factId, snapshot) =>
    apiFetch(`/api/cim/engagements/${eid}/ledger/facts/${factId}${snapshot != null ? `?snapshot=${snapshot}` : ''}`, { cacheMs: 0 }),
  cimLedgerDiscrepancies: (eid, status = 'all') =>
    apiFetch(`/api/cim/engagements/${eid}/ledger/discrepancies?status=${status}`, { cacheMs: 0 }),
  cimLedgerFreezePlan: (eid) => apiFetch(`/api/cim/engagements/${eid}/ledger/freeze`, { cacheMs: 0 }),
  cimLedgerSnapshots: (eid) => apiFetch(`/api/cim/engagements/${eid}/ledger/snapshots`, { cacheMs: 0 }),
  cimLedgerSettings: (eid, body) => apiFetch(`/api/cim/engagements/${eid}/ledger/settings`, { method: 'PATCH', body }),
  cimLedgerExtract: (eid, mode) => apiFetch(`/api/cim/engagements/${eid}/ledger/extract`, { method: 'POST', body: { mode } }),
  cimLedgerJob: (eid, jobId, { after = 0, eventsOnly = false } = {}) =>
    apiFetch(`/api/cim/engagements/${eid}/jobs/${jobId}?after=${after}${eventsOnly ? '&events_only=true' : ''}`, { cacheMs: 0 }),
  cimLedgerCancel: (eid, jobId) => apiFetch(`/api/cim/engagements/${eid}/jobs/${jobId}/cancel`, { method: 'POST' }),
  cimLedgerFactAction: (eid, factId, action, reason) =>
    apiFetch(`/api/cim/engagements/${eid}/ledger/facts/${factId}`, { method: 'PATCH', body: { action, reason: reason || null } }),
  // scope names the click in the audit line: clean | per_management | statement
  cimLedgerBulk: (eid, action, factIds, reason, scope) =>
    apiFetch(`/api/cim/engagements/${eid}/ledger/facts/bulk`, { method: 'POST', body: { action, fact_ids: factIds, reason: reason || null, scope: scope || null } }),
  // Approve clean: the ids across every statement, counted per statement (read on the click)
  cimLedgerClean: (eid) => apiFetch(`/api/cim/engagements/${eid}/ledger/clean`, { cacheMs: 0 }),
  cimLedgerUseRanking: (eid, aliasSet, reason) =>
    apiFetch(`/api/cim/engagements/${eid}/ledger/customers/use-ranking`, { method: 'POST', body: { alias_set: aliasSet, reason } }),
  cimLedgerResolve: (eid, code, body) =>
    apiFetch(`/api/cim/engagements/${eid}/ledger/discrepancies/${encodeURIComponent(code)}/resolve`, { method: 'POST', body }),
  cimLedgerResolveMany: (eid, codes, reason) =>
    apiFetch(`/api/cim/engagements/${eid}/ledger/discrepancies/resolve-many`, { method: 'POST', body: { codes, side: 'statement', reason } }),
  cimLedgerReopenDcode: (eid, code, reason) =>
    apiFetch(`/api/cim/engagements/${eid}/ledger/discrepancies/${encodeURIComponent(code)}/reopen`, { method: 'POST', body: { reason } }),
  cimLedgerFreeze: (eid, body) => apiFetch(`/api/cim/engagements/${eid}/ledger/snapshots`, { method: 'POST', body }),
  // CIM sections (routes/cim_sections.py) — the Command Center's Sections tab.
  // Reads are cacheMs: 0 (polled while a build runs; image URLs are signed per
  // request). A section job's progress and cancel use cimLedgerJob / cimLedgerCancel.
  cimSections: (eid) => apiFetch(`/api/cim/engagements/${eid}/sections`, { cacheMs: 0 }),
  cimSection: (eid, n) => apiFetch(`/api/cim/engagements/${eid}/sections/${n}`, { cacheMs: 0 }),
  cimSectionVersion: (eid, n, vid) => apiFetch(`/api/cim/engagements/${eid}/sections/${n}/versions/${vid}`, { cacheMs: 0 }),
  cimSectionImages: (eid, n, vid, mode) =>
    apiFetch(`/api/cim/engagements/${eid}/sections/${n}/versions/${vid}/images?mode=${encodeURIComponent(mode)}`, { cacheMs: 0 }),
  cimSectionCompare: (eid, n, vid, withId) =>
    apiFetch(`/api/cim/engagements/${eid}/sections/${n}/versions/${vid}/compare?with=${encodeURIComponent(withId)}`, { cacheMs: 0 }),
  cimSectionBuild: (eid, n, body) => apiFetch(`/api/cim/engagements/${eid}/sections/${n}/builds`, { method: 'POST', body }),
  cimSectionCritique: (eid, n, vid) => apiFetch(`/api/cim/engagements/${eid}/sections/${n}/versions/${vid}/critique`, { method: 'POST' }),
  cimSectionApproval: (eid, n, body) => apiFetch(`/api/cim/engagements/${eid}/sections/${n}/approvals`, { method: 'POST', body }),
  // Approve clean: [{section, version_id}] seen with nothing to acknowledge → {approved, skipped}
  cimSectionsApproveClean: (eid, sections) =>
    apiFetch(`/api/cim/engagements/${eid}/sections/approve-clean`, { method: 'POST', body: { sections } }),
  // Build all ({}) / Build selected ({sections}) and a chain's continue | skip | cancel.
  cimSectionsBuildAll: (eid, body) => apiFetch(`/api/cim/engagements/${eid}/sections/build-all`, { method: 'POST', body: body || {} }),
  cimSectionsBuildLock: (eid) => apiFetch(`/api/cim/engagements/${eid}/build-lock`, { cacheMs: 0 }),
  // {sections, assignee_id | null, due_date?} — a section's open task is its assignment
  cimSectionAssign: (eid, body) => apiFetch(`/api/cim/engagements/${eid}/sections/assignments`, { method: 'POST', body }),
  cimChainAction: (eid, cid, action) => apiFetch(`/api/cim/engagements/${eid}/chains/${cid}/${action}`, { method: 'POST' }),
  // CIM book (routes/cim_book.py) — the Command Center's Book tab. Reads are
  // cacheMs: 0; a render job is followed and cancelled through cimLedgerJob /
  // cimLedgerCancel (the generic job routes).
  cimBook: (eid) => apiFetch(`/api/cim/engagements/${eid}/book`, { cacheMs: 0 }),
  cimBookSettings: (eid, body) => apiFetch(`/api/cim/engagements/${eid}/book/settings`, { method: 'PATCH', body }),
  cimBookRender: (eid) => apiFetch(`/api/cim/engagements/${eid}/book/documents`, { method: 'POST', body: {} }),
  cimBookDocument: (eid, docId) => apiFetch(`/api/cim/engagements/${eid}/book/documents/${docId}`, { cacheMs: 0 }),
  cimBookPages: (eid, docId) => apiFetch(`/api/cim/engagements/${eid}/book/documents/${docId}/pages`, { cacheMs: 0 }),
  cimBookFileUrl: (eid, fileId) => apiFetch(`/api/cim/engagements/${eid}/book/files/${fileId}?json=true`, { cacheMs: 0 }),
  // 7c: the team PDF (multipart, raw fetch: the browser writes the boundary;
  // errors as apiFetch's "<status> <text>: <body>"), audit again, switching the
  // buyer PDF, final approval / withdraw. The document GET carries the rest.
  uploadCimBookPdf: async (eid, docId, file) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers = { Authorization: `Bearer ${await tokenForRequest()}` };
    const activeUser = localStorage.getItem('atlas.activeUserId');
    if (activeUser) headers['X-Active-User'] = activeUser;
    const res = await fetch(`${getBaseUrl()}/api/cim/engagements/${eid}/book/documents/${docId}/uploads`, {
      method: 'POST', headers, body: fd,
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}: ${await res.text()}`);
    return res.json();
  },
  cimBookAuditAgain: (eid, docId, uploadId) => apiFetch(`/api/cim/engagements/${eid}/book/documents/${docId}/uploads/${uploadId}/audit`, { method: 'POST' }),
  cimBookSwitch: (eid, docId, body) => apiFetch(`/api/cim/engagements/${eid}/book/documents/${docId}/deliverable`, { method: 'POST', body }),
  cimBookApproval: (eid, docId, body) => apiFetch(`/api/cim/engagements/${eid}/book/documents/${docId}/approvals`, { method: 'POST', body }),
  // The signed URL already carries download=<name> (saved, never shown
  // inline); the server logs every download. The iOS shell opens it in its
  // Safari sheet.
  openCimBookFile: async (eid, fileId) => {
    const { url } = await window.AtlasApi.cimBookFileUrl(eid, fileId);
    const native = window.AtlasNative && window.AtlasNative.isNative ? window.AtlasNative : null;
    if (native) { await native.openExternal(url); return; }
    window.location.assign(url);
  },
  // Bulk update from a spreadsheet (routes/imports.py) — kind is companies |
  // contacts | deals. Preview writes nothing; apply takes the preview's rows
  // in chunks of <= 100.
  previewImport: async (kind, file) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers = { Authorization: `Bearer ${await tokenForRequest()}` };
    const activeUser = localStorage.getItem('atlas.activeUserId');
    if (activeUser) headers['X-Active-User'] = activeUser;
    const res = await fetch(`${getBaseUrl()}/api/imports/${kind}/preview`, { method: 'POST', headers, body: fd });
    if (!res.ok) {
      let detail = res.statusText;
      try { detail = (await res.json()).detail || detail; } catch (_) {}
      throw new Error(detail);
    }
    return res.json();
  },
  applyImport: (kind, body) => apiFetch(`/api/imports/${kind}/apply`, { method: 'POST', body, timeoutMs: 180_000 }),
  // Sellside buyer outreach tracker (routes/outreach.py). The tracker upload
  // mirrors previewImport/applyImport; firm-level fields go through
  // updateBuyerEngagement.
  outreachTracker: (engagementId) => apiFetch(`/api/outreach/${engagementId}`, { cacheMs: 0 }),
  previewOutreachImport: async (engagementId, file) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers = { Authorization: `Bearer ${await tokenForRequest()}` };
    const activeUser = localStorage.getItem('atlas.activeUserId');
    if (activeUser) headers['X-Active-User'] = activeUser;
    const res = await fetch(`${getBaseUrl()}/api/outreach/${engagementId}/import/preview`, { method: 'POST', headers, body: fd });
    if (!res.ok) {
      let detail = res.statusText;
      try { detail = (await res.json()).detail || detail; } catch (_) {}
      throw new Error(detail);
    }
    return res.json();
  },
  applyOutreachImport: (engagementId, body) =>
    apiFetch(`/api/outreach/${engagementId}/import/apply`, { method: 'POST', body, timeoutMs: 180_000 }),
  addOutreachRecipient: (engagementId, data) =>
    apiFetch(`/api/outreach/${engagementId}/recipients`, { method: 'POST', body: JSON.stringify(data) }),
  updateOutreachRecipient: (id, data) =>
    apiFetch(`/api/outreach/recipients/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  removeOutreachRecipient: (id) => apiFetch(`/api/outreach/recipients/${id}`, { method: 'DELETE' }),
  // The deal's SuperSend campaign(s): linking fills the tracker's email
  // columns and stops the campaign running as seller marketing.
  outreachCampaignCandidates: (engagementId) =>
    apiFetch(`/api/outreach/${engagementId}/campaigns/candidates`, { cacheMs: 0 }),
  linkOutreachCampaign: (engagementId, data) =>
    apiFetch(`/api/outreach/${engagementId}/campaigns`, { method: 'POST', body: JSON.stringify(data), timeoutMs: 120_000 }),
  pushOutreachCampaign: (engagementId, linkId, recipientIds) =>
    apiFetch(`/api/outreach/${engagementId}/campaigns/${linkId}/push`, { method: 'POST', body: JSON.stringify({ recipient_ids: recipientIds }), timeoutMs: 120_000 }),
  unlinkOutreachCampaign: (engagementId, linkId) =>
    apiFetch(`/api/outreach/${engagementId}/campaigns/${linkId}`, { method: 'DELETE' }),
  updateOutreachProcess: (engagementId, data) =>
    apiFetch(`/api/outreach/${engagementId}/process`, { method: 'PUT', body: JSON.stringify(data) }),
  dismissOutreachSuggestion: (buyerEngagementId, key) =>
    apiFetch(`/api/outreach/firms/${buyerEngagementId}/dismiss-suggestion`, { method: 'POST', body: JSON.stringify({ key }) }),
  fileOutreachMail: (engagementId, noteId, belongs) =>
    apiFetch(`/api/outreach/${engagementId}/mail/${noteId}/file`, { method: 'POST', body: JSON.stringify({ belongs }) }),
  outreachThread: (engagementId, buyerEngagementId) =>
    apiFetch(`/api/outreach/${engagementId}/thread/${buyerEngagementId}`, { cacheMs: 0 }),
  deleteMemberFile: (memberId, attachmentId) =>
    apiFetch(`/api/team/members/${memberId}/files/${attachmentId}`, { method: 'DELETE' }),

  // notes (Phase A — first-class object)
  // Calendar (merged Outlook + Fireflies meetings)
  calendarEvents: (p = {}) => apiFetch(`/api/calendar/events${qs(p)}`),
  calendarPeople: () => apiFetch('/api/calendar/people', { cacheMs: 300_000 }),

  listNotes:    (p = {}) => apiFetch(`/api/notes${qs({ page: 1, limit: 100, ...p })}`),
  getNote:      (id) => apiFetch(`/api/notes/${id}`),
  // Stored meeting transcript. Its own call because the sentence array is
  // large and only wanted when someone actually opens it.
  noteTranscript: (id) => apiFetch(`/api/notes/${id}/transcript`),
  createNote:   (data) => apiFetch('/api/notes', { method: 'POST', body: JSON.stringify(data) }),
  updateNote:   (id, data) => apiFetch(`/api/notes/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteNote:   (id) => apiFetch(`/api/notes/${id}`, { method: 'DELETE' }),
  // Email threads: lazy AI summary for emails sharing a Graph conversation_id.
  // Server caches it and only re-summarizes when the thread grew ("refresh on view").
  emailThreadSummary: (conversation_id) =>
    apiFetch('/api/email-threads/summary', { method: 'POST', body: JSON.stringify({ conversation_id }) }),
  // Per-note AI previews for the collapsed note cards. Returns cached
  // summaries + generates a bounded batch of missing ones per call; callers
  // re-request while `pending` > 0 (see useNoteAiPreviews).
  noteAiPreviews: (note_ids) =>
    apiFetch('/api/notes/ai-previews', { method: 'POST', body: JSON.stringify({ note_ids }) }),

  // tasks (Phase 3 §4.2)
  listTasks:   (p = {}) => apiFetch(`/api/tasks${qs({ page: 1, limit: 100, ...p })}`),
  // NL composer: "remind me every monday to check on X" → structured draft.
  // Nothing is saved; the client confirms the draft, then calls createTask.
  // extra = { tagged_contact_id, tagged_company_id } from composer @-mentions.
  parseTask:   (text, extra = {}) => apiFetch('/api/tasks/parse', { method: 'POST', body: JSON.stringify({ text, ...extra }) }),
  createTask:  (data) => apiFetch('/api/tasks', { method: 'POST', body: JSON.stringify(data) }),
  updateTask:  (id, data) => apiFetch(`/api/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteTask:  (id) => apiFetch(`/api/tasks/${id}`, { method: 'DELETE' }),
  // Automations: AI-suggested tasks awaiting confirm + the rule registry.
  listTaskProposals: () => apiFetch('/api/task-proposals'),
  actTaskProposal:   (id, action, body) => apiFetch(`/api/task-proposals/${id}/${action}`, { method: 'POST', body: JSON.stringify(body || {}) }),
  listTaskRules:     () => apiFetch('/api/task-rules'),
  // Atlas Inbox: forwarded emails awaiting a filing decision (Slack parity).
  listInboxPending:   () => apiFetch('/api/integrations/atlas-inbox/pending'),
  actInboxSubmission: (id, verb, arg) => apiFetch(`/api/integrations/atlas-inbox/submissions/${id}/act`, { method: 'POST', body: JSON.stringify({ verb, arg: arg || null }) }),
  // Releases — versioned What's-new notes.
  listReleases:      (p = {}) => apiFetch(`/api/releases${qs(p)}`),
  releasesState:     () => apiFetch('/api/releases/state'),
  markReleasesSeen:  () => apiFetch('/api/releases/seen', { method: 'POST' }),
  createRelease:     (data) => apiFetch('/api/releases', { method: 'POST', body: JSON.stringify(data) }),
  updateRelease:     (id, data) => apiFetch(`/api/releases/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteRelease:     (id) => apiFetch(`/api/releases/${id}`, { method: 'DELETE' }),
  publishRelease:    (id) => apiFetch(`/api/releases/${id}/publish`, { method: 'POST' }),
  addReleaseItem:    (id, data) => apiFetch(`/api/releases/${id}/items`, { method: 'POST', body: JSON.stringify(data) }),
  updateReleaseItem: (id, iid, data) => apiFetch(`/api/releases/${id}/items/${iid}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteReleaseItem: (id, iid) => apiFetch(`/api/releases/${id}/items/${iid}`, { method: 'DELETE' }),
  setTaskRulePref:   (key, disabled) => apiFetch(`/api/task-rules/${key}`, { method: 'PUT', body: JSON.stringify({ disabled }) }),

  // stage history (Phase 3 §4.3) — read-only
  listStageHistory: (engagementId) => apiFetch(`/api/stage-history${qs({ engagement_id: engagementId })}`),

  // global search
  search: (q, limit = 12) => apiFetch(`/api/search${qs({ q, limit })}`),
  // {type: 'company'|'deal'|'contact', id} for a record ID (#/id/<n> links).
  recordByNumber: (n) => apiFetch(`/api/search/record/${encodeURIComponent(n)}`),

  // Phase 3 registry (read-only placeholder endpoints)
  listBuyers:               (p = {}) => apiFetch(`/api/buyers${qs({ limit: 50, ...p })}`),
  // Derived buyer directory: firm-level aggregation of buyer_buyside
  // engagements (the real buyer universe until a structured Phase-5 import).
  buyerDirectory:           (p = {}) => apiFetch(`/api/buyers/directory${qs({ limit: 1000, ...p })}`),
  // One row per buyer firm, aggregated from buyer_engagements (same population
  // as buyerEngagementsForCompany) — parent rows for the grouped Buyers tab.
  buyerFirms:               (p = {}) => apiFetch(`/api/buyers/firms${qs({ limit: 2000, ...p })}`),
  // The buyer roster: one row per firm joining the `buyers` ICP + agreement
  // profile (131) to that firm's deal activity. Powers the Buyers page. All
  // filtering is server-side; the endpoint returns one page and the grid
  // windows it. `ebitda`/`revenue` are ABSOLUTE dollars (051).
  buyerRoster:              (p = {}) => apiFetch(`/api/buyers/roster${qs({ limit: 3000, ...p })}`),
  // The match QUEUE: live deals past the D/S call, each with its best-matching
  // buyers already scored. The Match tab's landing view — no deal to look up.
  buyerMatchQueue:          (p = {}) => apiFetch(`/api/buyers/match-queue${qs({ top: 3, limit: 25, ...p })}`),
  // Rank buyers against one seller deal, with the reasoning attached.
  // Scores every buyer — those with no stated band/industry score NEUTRAL,
  // not zero, so the PEG universe isn't buried; check `confidence`.
  matchBuyersToDeal:        (p = {}) => apiFetch(`/api/buyers/match${qs({ limit: 25, ...p })}`),
  // What changed on buyer websites since we last looked (133) — the review
  // queue behind the Buyers page's Changes tab.
  buyerSiteChanges:         (p = {}) => apiFetch(`/api/buyers/site-changes${qs({ limit: 500, ...p })}`),
  reviewBuyerSiteChange:    (id) => apiFetch(`/api/buyers/site-changes/${id}/review`, { method: 'POST' }),
  // What a buyer already OWNS, read off its portfolio page (144). Returns
  // `read.status` alongside the list — an empty list means "they publish
  // none" ONLY when that says 'ok'; 'unreadable' / 'no_page' / 'never_read'
  // are the other three, and they are not the same answer.
  buyerPortfolio:           (p = {}) => apiFetch(`/api/buyers/portfolio${qs({ limit: 500, ...p })}`),
  // Companies a buyer's portfolio page says it bought (202609221400). The
  // review queue behind the Buyers page's Acquisitions tab; `decide` with
  // 'confirmed' flips companies.is_acquired, 'rejected' leaves it alone.
  buyerAcquisitions:        (p = {}) => apiFetch(`/api/buyers/acquisitions${qs({ limit: 500, ...p })}`),
  // Buyer intent signals (202609230900): exits, new holdings, team moves,
  // criteria changes, acquisitions, fund closes, press — per buyer, routed to
  // Atlas industries. `verb` = review | dismiss | reopen.
  buyerIntent:              (p = {}) => apiFetch(`/api/buyers/intent${qs({ limit: 300, ...p })}`),
  actBuyerSignal:           (id, verb) => apiFetch(`/api/buyers/intent/${id}/${verb}`, { method: 'POST' }),
  decideBuyerAcquisition:   (id, decision) => apiFetch(`/api/buyers/acquisitions/${id}/decide`, { method: 'POST', body: JSON.stringify({ decision }) }),
  // Pending acquisition records for ONE company — the "Acquired? · confirm"
  // pill on the company and deal headers (views/acquisition_confirm.jsx).
  // Never cached: it is re-read right after a decision.
  acquisitionsToConfirm:    (companyId) => apiFetch(`/api/buyers/acquisitions${qs({ company_id: companyId, state: 'pending', limit: 10 })}`, { cacheMs: 0 }),
  // Firms discovered on the web that are not on the roster (202609221500).
  // Accept creates the companies + buyers pair (Atlas-only); the batch call
  // takes a filter ({ confidence, buyer_types, atlas_only, limit }).
  buyerCandidates:          (p = {}) => apiFetch(`/api/buyers/candidates${qs({ limit: 500, ...p })}`),
  decideBuyerCandidate:     (id, decision) => apiFetch(`/api/buyers/candidates/${id}/decide`, { method: 'POST', body: JSON.stringify({ decision }) }),
  acceptBuyerCandidates:    (filter) => apiFetch('/api/buyers/candidates/accept-batch', { method: 'POST', body: JSON.stringify(filter || {}) }),
  // The people on a buyer's team page, ranked for outreach (202609221600);
  // `gaps` = what the firm's site does not state.
  buyerTeam:                (p = {}) => apiFetch(`/api/buyers/team${qs(p)}`),
  buyerPasses:              (p = {}) => apiFetch(`/api/buyers/passes${qs(p)}`),
  enrichTeamMember:         (id) => apiFetch(`/api/buyers/team/${id}/enrich`, { method: 'POST' }),
  addTeamMemberContact:     (id) => apiFetch(`/api/buyers/team/${id}/add-contact`, { method: 'POST' }),
  // The buyer page (202609231400)
  getBuyer:                 (p = {}, opts) => apiFetch(`/api/buyers/one${qs(p)}`, opts),
  updateBuyer:              (id, data) => apiFetch(`/api/buyers/one/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  sendBuyerDeal:            (id, data) => apiFetch(`/api/buyers/one/${id}/send`, { method: 'POST', body: JSON.stringify(data) }),
  // A firm onto the roster by hand, and reading a file it sent into its
  // profile (app/buyer_intake.py). read-file returns at once; the read runs
  // server-side and getBuyer's document_job says when it is done.
  addBuyerFirm:             (companyId) => apiFetch('/api/buyers/add-firm', { method: 'POST', body: JSON.stringify({ company_id: companyId }) }),
  buyerFiles:               (id) => apiFetch(`/api/buyers/one/${id}/files`, { cacheMs: 0 }),
  readBuyerFile:            (id, attachmentId) => apiFetch(`/api/buyers/one/${id}/read-file`, { method: 'POST', body: JSON.stringify({ attachment_id: attachmentId }) }),
  // Create a buyer engagement: the buyer on the seller deal it is buying
  // into + its pursuit record (app/buyer_add.py). A buyer already on the
  // deal is updated, never duplicated. → { row, added, pursuit_created, … }
  addBuyerToDeal:           (data) => apiFetch('/api/buyers/add-to-deal', { method: 'POST', body: JSON.stringify(data) }),
  // Interested buyers on one seller engagement (joined to buyer name + their
  // total deal count) — powers the deal page's buyer board.
  buyersForEngagement:      (engagementId) => apiFetch(`/api/buyers/for-engagement${qs({ engagement_id: engagementId })}`),
  // Seller brief (202609231000): the stored read of the seller behind a deal.
  sellerProfile:            (p = {}) => apiFetch(`/api/sellers/profile${qs(p)}`),
  dealComps:                (engagementId) => apiFetch(`/api/comps${qs({ engagement_id: engagementId })}`),
  companyComps:             (companyId) => apiFetch(`/api/comps${qs({ company_id: companyId })}`),
  compsNearby:              (companyId, p = {}) => apiFetch(`/api/comps/nearby${qs({ company_id: companyId, ...p })}`),
  compsNearbySweep:         (companyId) => apiFetch(`/api/comps/nearby/sweep${qs({ company_id: companyId })}`, { method: 'POST' }),
  // cacheMs 0: the panel polls this while a check runs (a 30 s cached GET froze it).
  companyOwnership:         (companyId) => apiFetch(`/api/companies/${companyId}/ownership`, { cacheMs: 0 }),
  refreshCompanyOwnership:  (companyId) => apiFetch(`/api/companies/${companyId}/ownership/refresh`, { method: 'POST' }),
  patchCompanyOwnership:    (companyId, data) => apiFetch(`/api/companies/${companyId}/ownership`, { method: 'PATCH', body: JSON.stringify(data) }),
  // A deal id (string) or { company_id } for a prospect with no deal.
  refreshSellerProfile:     (ref) => apiFetch('/api/sellers/profile/refresh', { method: 'POST', body: JSON.stringify(typeof ref === 'string' ? { engagement_id: ref } : ref) }),
  patchSellerProfile:       (companyId, patch) => apiFetch(`/api/sellers/profile/${companyId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  // Find buyers for a deal (202609222000): start → poll → export / add picks.
  startBuyerSearch:         (engagementId, p = {}) => apiFetch('/api/buyers/search', { method: 'POST', body: JSON.stringify({ engagement_id: engagementId, ...p }) }),
  // Both are polled while a run goes: cacheMs 0, or a 30 s cache hit hands back the same object.
  listBuyerSearches:        (engagementId) => apiFetch(`/api/buyers/search${qs({ engagement_id: engagementId })}`, { cacheMs: 0 }),
  getBuyerSearch:           (id) => apiFetch(`/api/buyers/search/${id}`, { cacheMs: 0 }),
  listBuyerListImports:     (engagementId) => apiFetch(`/api/buyers/search/imports${qs({ engagement_id: engagementId })}`, { cacheMs: 0 }),
  deleteBuyerListImport:    (id) => apiFetch(`/api/buyers/search/imports/${id}`, { method: 'DELETE' }),
  importBuyerList: async (engagementId, source, file) => {
    // Multipart — apiFetch would force a JSON content type.
    const fd = new FormData();
    fd.append('engagement_id', engagementId);
    fd.append('source', source);
    fd.append('file', file, file.name);
    const headers = { Authorization: `Bearer ${getToken()}` };
    const activeUser = localStorage.getItem('atlas.activeUserId');
    if (activeUser) headers['X-Active-User'] = activeUser;
    const res = await fetch(`${getBaseUrl()}/api/buyers/search/imports`, { method: 'POST', headers, body: fd });
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try { msg = (await res.json()).detail || msg; } catch (_) {}
      throw new Error(msg);
    }
    return res.json();
  },
  exportBuyerSearch:        (id) => exportFetch(`/api/buyers/search/${id}/export`, {}, 'POST', 'buyer-search.xlsx'),
  addBuyerSearchResults:    (id, resultIds) => apiFetch(`/api/buyers/search/${id}/add`, { method: 'POST', body: JSON.stringify({ result_ids: resultIds }) }),
  // Seller engagements where a company appears as an interested buyer — the
  // inverse of buyersForEngagement; powers the company profile's Buyer
  // Engagements panel.
  buyerEngagementsForCompany: (companyId) => apiFetch(`/api/buyers/for-company${qs({ company_id: companyId })}`),
  // Firm-level buyer profile (tier, size band, industries, criteria) — the
  // buyers row fed by the tag derivation + the Buyer Report import.
  buyerProfile:             (companyId) => apiFetch(`/api/buyers/profile${qs({ company_id: companyId })}`),
  // Inline edits on the deal page's Interested-buyers grid. Fields sent here
  // are recorded in manual_fields server-side so sync never overwrites them.
  updateBuyerEngagement:    (id, data) => apiFetch(`/api/buyers/engagement-row/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  // LOI comparison on a seller deal (backend app/loi_compare.py). Polled while a
  // letter is being read, so never cached.
  lois:                     (id) => apiFetch(`/api/lois/${id}`, { cacheMs: 0 }),
  loiLetters:               (id) => apiFetch(`/api/lois/${id}/letters`, { cacheMs: 0 }),
  loiEvidence:              (id, beId) => apiFetch(`/api/lois/${id}/buyers/${beId}/evidence`, { cacheMs: 0 }),
  pickLoiLetter:            (id, beId, attachmentId) => apiFetch(`/api/lois/${id}/buyers/${beId}/letter`, { method: 'PUT', body: JSON.stringify({ attachment_id: attachmentId }) }),
  rereadLoi:                (id, beId) => apiFetch(`/api/lois/${id}/buyers/${beId}/read`, { method: 'POST' }),
  editLoiTerm:              (id, beId, body) => apiFetch(`/api/lois/${id}/buyers/${beId}/terms`, { method: 'PATCH', body: JSON.stringify(body) }),
  exportLois:               (id) => exportFetch(`/api/lois/${id}/export.xlsx`, null, 'GET', 'LOI comparison.xlsx'),
  loiRules:                 () => apiFetch('/api/lois/rules', { cacheMs: 0 }),
  saveLoiRules:             (items) => apiFetch('/api/lois/rules', { method: 'PUT', body: JSON.stringify({ items }) }),
  uploadLoiLetter: async (id, beId, file) => {
    // Multipart — apiFetch would force a JSON content type.
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers = { Authorization: `Bearer ${await tokenForRequest()}` };
    const activeUser = localStorage.getItem('atlas.activeUserId');
    if (activeUser) headers['X-Active-User'] = activeUser;
    const res = await fetch(`${getBaseUrl()}/api/lois/${id}/buyers/${beId}/upload`, { method: 'POST', headers, body: fd });
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try { msg = (await res.json()).detail || msg; } catch (_) {}
      throw new Error(msg);
    }
    return res.json();
  },
  // A buyer's pursuit of a seller deal → one DealCloud stage key
  // (app/buyer_pursuit.PURSUIT_STAGES). By roster row (opens the pursuit
  // record when there is none) or by the pursuit engagement itself.
  setBuyerPursuitStage:     (id, stage) => apiFetch(`/api/buyers/engagement-row/${id}/pursuit-stage`, { method: 'POST', body: JSON.stringify({ stage }) }),
  setEngagementPursuitStage: (id, stage) => apiFetch(`/api/engagements/${id}/pursuit-stage`, { method: 'POST', body: JSON.stringify({ stage }) }),
  listBuyerEngagements:     (p = {}) => apiFetch(`/api/buyer-engagements${qs({ limit: 50, ...p })}`),
  listExitReadinessScores:  (p = {}) => apiFetch(`/api/exit-readiness-scores${qs({ limit: 50, ...p })}`),
  listAiCallSummaries:      (p = {}) => apiFetch(`/api/ai-call-summaries${qs({ limit: 50, ...p })}`),
  listFollowUpDrafts:       (p = {}) => apiFetch(`/api/follow-up-drafts${qs({ limit: 50, ...p })}`),
  listGppPartners:          (p = {}) => apiFetch(`/api/gpp-partners${qs({ limit: 50, ...p })}`),
  // GPP Partners → Scoreboard tab: stage counts, seasoning, live referral →
  // deal → fee attribution (app/gpp_scoreboard.py). Optional owner_id.
  gppScoreboard:            (p = {}) => apiFetch(`/api/gpp-partners/scoreboard${qs(p)}`),
  // MyExit Assessment runs for a company. Rows carry `tool_url` — a freshly
  // signed same-origin link that opens the assessment page (resume / view).
  // Never cache: the panel reloads right after an upload and on tab focus
  // (back from the tool), and a 30s-stale list hides the row just created.
  listMyExitAssessments:    (companyId) => apiFetch(`/api/myexit${qs({ company_id: companyId })}`, { cacheMs: 0 }),
  createMyExitAssessment:   (data) => apiFetch('/api/myexit', { method: 'POST', body: JSON.stringify(data) }),
  deleteMyExitAssessment:   (id) => apiFetch(`/api/myexit/${id}`, { method: 'DELETE' }),
  // File the results PDF from an assessment run outside Atlas — records a
  // completed run (source='upload') with the PDF on its completion note.
  // Raw fetch like uploadAttachments: multipart needs the browser's own
  // boundary and apiFetch forces a JSON content-type.
  uploadMyExitAssessment: async (fd) => {
    const headers = { Authorization: `Bearer ${await tokenForRequest()}` };
    const activeUser = localStorage.getItem('atlas.activeUserId');
    if (activeUser) headers['X-Active-User'] = activeUser;
    const res = await fetch(`${getBaseUrl()}/api/myexit/upload`, { method: 'POST', headers, body: fd });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}: ${await res.text()}`);
    const created = await res.json();
    // The PDF lands in the company's Files panel via the completion note.
    window.dispatchEvent(new CustomEvent('atlas-attachments-changed'));
    return created;
  },
  // Single-row helpers used by the contact-detail GPP panel.
  getGppPartnerByContact:   (contactId) => apiFetch(`/api/gpp-partners/by-contact/${contactId}`),
  gppPartnerReferralsSent:  (partnerId) => apiFetch(`/api/gpp-partners/${partnerId}/referrals-sent`),
  createGppPartner:         (data) => apiFetch('/api/gpp-partners', { method: 'POST', body: JSON.stringify(data) }),
  updateGppPartner:         (id, data) => apiFetch(`/api/gpp-partners/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  removeGppPartner:         (id) => apiFetch(`/api/gpp-partners/${id}`, { method: 'DELETE' }),

  // Referral partners (Brand Builder list). Adds/edits round-trip to
  // DealCloud via the write-back outbox (tags + BrandListAddedDate).
  listReferralPartners:        (p = {}) => apiFetch(`/api/referral-partners${qs({ limit: 500, ...p })}`),
  getReferralPartnerByContact: (contactId) => apiFetch(`/api/referral-partners/by-contact/${contactId}`),
  createReferralPartner:       (data) => apiFetch('/api/referral-partners', { method: 'POST', body: JSON.stringify(data) }),
  updateReferralPartner:       (id, data) => apiFetch(`/api/referral-partners/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  removeReferralPartner:       (id) => apiFetch(`/api/referral-partners/${id}`, { method: 'DELETE' }),

  // Tags catalog. The DC backfill leaves the catalog at ~3.3K rows; we pass
  // a high default limit so the Settings page sees the full slice and its
  // KPI counters are accurate. The endpoint paginates if a caller passes a
  // tighter limit explicitly.
  listTags:            (p = {}) => apiFetch(`/api/tags${qs({ limit: 500, ...p })}`, { cacheMs: 5 * 60_000 }),
  updateTag:           (id, data) => apiFetch(`/api/tags/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  // Bulk-archive (and bulk-recategorize). Pass either tag_ids OR
  // max_contacts; see backend for semantics.
  bulkUpdateTags:      (data) => apiFetch('/api/tags/bulk-update', { method: 'POST', body: JSON.stringify(data) }),
  // Claude-Haiku-backed kind suggestion for one tag. Never auto-applies.
  suggestTagKind:      (id) => apiFetch(`/api/tags/${id}/suggest`, { method: 'POST' }),
  // Per-contact tags (contact_tags join). Add/remove return the contact's
  // full tag list so the UI can swap state without a follow-up fetch.
  listContactTags:     (contactId) => apiFetch(`/api/contacts/${contactId}/tags`),
  // Additional email addresses (contact_emails) + soft merge. Adding an
  // address answers 409 with `detail.contact` when another contact owns it —
  // the page offers to merge that contact into this one.
  listContactEmails:   (contactId) => apiFetch(`/api/contacts/${contactId}/emails`),
  addContactEmail:     (contactId, body) => apiFetch(`/api/contacts/${contactId}/emails`, { method: 'POST', body: JSON.stringify(body) }),
  removeContactEmail:  (contactId, emailId) => apiFetch(`/api/contacts/${contactId}/emails/${emailId}`, { method: 'DELETE' }),
  mergeContacts:       (keepId, mergeId) => apiFetch(`/api/contacts/${keepId}/merge`, { method: 'POST', body: JSON.stringify({ merge_id: mergeId }) }),
  addContactTag:       (contactId, tagId) => apiFetch(`/api/contacts/${contactId}/tags`, { method: 'POST', body: JSON.stringify({ tag_id: tagId }) }),
  removeContactTag:    (contactId, tagId) => apiFetch(`/api/contacts/${contactId}/tags/${tagId}`, { method: 'DELETE' }),

  // data quality (already exists server-side)
  dqSummary:           () => apiFetch('/api/data-quality/summary'),
  dqIndustryMismatch:  (limit = 150) => apiFetch(`/api/data-quality/industry-mismatches${qs({ limit })}`),
  dqTagCandidates:     (minCount = 1) => apiFetch(`/api/data-quality/tag-candidates${qs({ min_count: minCount })}`),
  dqCleanupTasks:      () => apiFetch('/api/data-quality/cleanup-tasks'),

  // team members (Phase 3 "users")
  // Lookup tables — change rarely. 5-min TTL.
  listTeamMembers: () => apiFetch('/api/team/members', { cacheMs: 5 * 60_000 }),
  getTeamMember: (id) => apiFetch(`/api/team/members/${id}`),
  createTeamMember: (data) => apiFetch('/api/team/members', { method: 'POST', body: JSON.stringify(data) }),
  updateTeamMember: (id, data) => apiFetch(`/api/team/members/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  listAccessRequests: () => apiFetch('/api/team/access-requests'),
  approveAccessRequest: (id) => apiFetch(`/api/team/access-requests/${id}/approve`, { method: 'POST' }),
  denyAccessRequest: (id) => apiFetch(`/api/team/access-requests/${id}/deny`, { method: 'POST' }),
  lookupTeamMemberSlack: (id) => apiFetch(`/api/team/members/${id}/slack-lookup`, { method: 'POST' }),
  deactivateTeamMember: (id) => apiFetch(`/api/team/members/${id}`, { method: 'DELETE' }),
  deleteTeamMember:    (id) => apiFetch(`/api/team/members/${id}?hard=true`, { method: 'DELETE' }),
  restoreTeamMember:   (id) => apiFetch(`/api/team/members/${id}/restore`, { method: 'POST' }),
  // Per-member capability overrides (Settings → Members → Permissions).
  // Uncached: an admin who just revoked someone must see it stick.
  memberPermissions: (id) => apiFetch(`/api/team/members/${id}/permissions`),
  setMemberPermissions: (id, overrides, reason) =>
    apiFetch(`/api/team/members/${id}/permissions`, {
      method: 'PUT', body: JSON.stringify({ overrides, reason: reason || null }),
    }),

  // teams
  listTeams:   () => apiFetch('/api/teams'),
  createTeam:  (data) => apiFetch('/api/teams', { method: 'POST', body: JSON.stringify(data) }),
  updateTeam:  (id, data) => apiFetch(`/api/teams/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  // hard=true removes the team and its memberships; default just deactivates.
  deleteTeam:  (id, { hard = false } = {}) => apiFetch(`/api/teams/${id}${hard ? '?hard=true' : ''}`, { method: 'DELETE' }),
  addTeamMembership:    (teamId, data) => apiFetch(`/api/teams/${teamId}/memberships`, { method: 'POST', body: JSON.stringify(data) }),
  updateTeamMembership: (teamId, mid, data) => apiFetch(`/api/teams/${teamId}/memberships/${mid}`, { method: 'PATCH', body: JSON.stringify(data) }),
  removeTeamMembership: (teamId, mid) => apiFetch(`/api/teams/${teamId}/memberships/${mid}`, { method: 'DELETE' }),

  // industries
  listIndustries:   () => apiFetch('/api/industries', { cacheMs: 5 * 60_000 }),
  // Company facts (202610061500): LinkedIn headcount, Google rating, qualification verdict.
  companyFacts:         (id) => apiFetch(`/api/company-facts/${id}`, { cacheMs: 0 }),   // re-read right after Refresh / Qualify
  refreshCompanyFacts:  (id) => apiFetch(`/api/company-facts/${id}/refresh`, { method: 'POST' }),
  qualifyCompany:       (id) => apiFetch(`/api/company-facts/${id}/qualify`, { method: 'POST' }),
  // Prospecting (202610061200): batches of leads through identify → ownership → qualify → contacts → send.
  // Never cached: the page polls these while a batch runs and re-reads them right after a decision.
  prospectingBatches:     (p = {}) => apiFetch(`/api/prospecting/batches${qs(p)}`, { cacheMs: 0 }),
  prospectingBatch:       (id) => apiFetch(`/api/prospecting/batches/${id}`, { cacheMs: 0 }),
  prospectingProspects:   (id, p = {}) => apiFetch(`/api/prospecting/batches/${id}/prospects${qs(p)}`, { cacheMs: 0 }),
  prospectingProspect:    (pid) => apiFetch(`/api/prospecting/prospects/${pid}`, { cacheMs: 0 }),
  // Intake checks every lead against Atlas inside the request (up to 500 a batch). Wait past the default 120 s so a
  // slow check is not shown as a failure while the batch was in fact created.
  prospectingCreateBatch: (data) => apiFetch('/api/prospecting/batches', { method: 'POST', body: JSON.stringify(data), timeoutMs: 300_000 }),
  prospectingUpload:      (file, name) => { const fd = new FormData(); fd.append('file', file); if (name) fd.append('name', name); return apiFetch('/api/prospecting/batches/upload', { method: 'POST', body: fd, timeoutMs: 300_000 }); },
  prospectingAdd:         (id, text) => apiFetch(`/api/prospecting/batches/${id}/add`, { method: 'POST', body: JSON.stringify({ text }), timeoutMs: 300_000 }),
  prospectingRun:         (id, data) => apiFetch(`/api/prospecting/batches/${id}/run`, { method: 'POST', body: JSON.stringify(data || {}) }),
  prospectingDecision:    (pid, data) => apiFetch(`/api/prospecting/prospects/${pid}/decision`, { method: 'POST', body: JSON.stringify(data) }),
  prospectingSend:        (pid) => apiFetch(`/api/prospecting/prospects/${pid}/send`, { method: 'POST' }),
  prospectingSendApproved:(id) => apiFetch(`/api/prospecting/batches/${id}/send`, { method: 'POST' }),
  // Lead discovery (202610071700): fill a batch from Apollo, Google Maps or
  // the Atlas lead pool. Options are cached; a preview is a real search.
  prospectingDiscoverOptions: () => apiFetch('/api/prospecting/discover/options', { cacheMs: 5 * 60_000 }),
  prospectingDiscoverPreview: (q) => apiFetch('/api/prospecting/discover/preview', { method: 'POST', body: JSON.stringify(q) }),
  prospectingDiscover:        (q) => apiFetch('/api/prospecting/discover', { method: 'POST', body: JSON.stringify(q) }),
  prospectingDecideMany:      (id, ids, decision) => apiFetch(`/api/prospecting/batches/${id}/decide`, { method: 'POST', body: JSON.stringify({ ids, decision }) }),
  prospectingRenameBatch:     (id, data) => apiFetch(`/api/prospecting/batches/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  prospectingDeleteBatch:     (id) => apiFetch(`/api/prospecting/batches/${id}`, { method: 'DELETE' }),
  prospectingFindOwners:      (pid) => apiFetch(`/api/prospecting/prospects/${pid}/find-owners`, { method: 'POST' }),
  // Industry pages (202611021400): one page per industry — the valuation
  // (saved by the valuation skill's connector tool), our numbers, the trade,
  // files, outreach. Never cached: a save or an upload must show at once.
  listIndustryPages:   () => apiFetch('/api/industry-pages', { cacheMs: 0 }),
  getIndustryPage:     (slug) => apiFetch(`/api/industry-pages/${encodeURIComponent(slug)}`, { cacheMs: 0 }),
  getIndustryVersion:  (slug, v) => apiFetch(`/api/industry-pages/${encodeURIComponent(slug)}/versions/${v}`),
  getIndustryMarket:   (slug) => apiFetch(`/api/industry-pages/${encodeURIComponent(slug)}/market`),
  makeIndustryVersionCurrent: (slug, v) => apiFetch(`/api/industry-pages/${encodeURIComponent(slug)}/versions/${v}/current`, { method: 'POST' }),
  // Atlas builds (202611022100): a draft until Manage industries approves it.
  approveIndustryVersion: (slug, v) => apiFetch(`/api/industry-pages/${encodeURIComponent(slug)}/versions/${v}/approve`, { method: 'POST' }),
  discardIndustryVersion: (slug, v) => apiFetch(`/api/industry-pages/${encodeURIComponent(slug)}/versions/${v}/discard`, { method: 'POST' }),
  getIndustryBuild:    (slug) => apiFetch(`/api/industry-pages/${encodeURIComponent(slug)}/build`, { cacheMs: 0 }),
  getIndustryBuildSuggestion: (slug) => apiFetch(`/api/industry-pages/${encodeURIComponent(slug)}/build/suggestion`, { cacheMs: 0 }),
  startIndustryBuild:  (slug, body) => apiFetch(`/api/industry-pages/${encodeURIComponent(slug)}/builds`, { method: 'POST', body: JSON.stringify(body || {}) }),
  cancelIndustryBuild: (slug, id) => apiFetch(`/api/industry-pages/${encodeURIComponent(slug)}/builds/${id}/cancel`, { method: 'POST' }),
  getMissingIndustryBuilds:   () => apiFetch('/api/industry-pages/missing-builds', { cacheMs: 0 }),
  queueMissingIndustryBuilds: () => apiFetch('/api/industry-pages/missing-builds', { method: 'POST' }),
  cancelQueuedIndustryBuilds: () => apiFetch('/api/industry-pages/queued-builds/cancel', { method: 'POST' }),
  removeIndustryFile:  (slug, attachmentId) => apiFetch(`/api/industry-pages/${encodeURIComponent(slug)}/files/${attachmentId}`, { method: 'DELETE' }),
  uploadIndustryFiles: async (slug, kind, fileList) => {
    // Raw fetch, like uploadMemberFiles: multipart needs the browser to
    // write its own boundary.
    const fd = new FormData();
    fd.append('kind', kind);
    for (const f of fileList) fd.append('files', f, f.name);
    const headers = { Authorization: `Bearer ${await tokenForRequest()}` };
    const res = await fetch(`${getBaseUrl()}/api/industry-pages/${encodeURIComponent(slug)}/files`, {
      method: 'POST', headers, body: fd,
    });
    if (!res.ok) {
      let detail = res.statusText;
      try { detail = (await res.json()).detail || detail; } catch (_) {}
      throw new Error(detail);
    }
    return res.json();
  },
  // Industry rules (202610052100): target / exclusions / criteria / floor / rubric.
  listIndustryProfiles: (p = {}) => apiFetch(`/api/industry-profiles${qs(p)}`),
  getIndustryProfile:   (id) => apiFetch(`/api/industry-profiles/by-industry/${id}`),
  saveIndustryProfile:  (id, data) => apiFetch(`/api/industry-profiles/by-industry/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  createIndustry:   (data) => apiFetch('/api/industries', { method: 'POST', body: JSON.stringify(data) }),
  updateIndustry:   (id, data) => apiFetch(`/api/industries/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteIndustry:   (id) => apiFetch(`/api/industries/${id}`, { method: 'DELETE' }),
  // Aliases — other DealCloud spellings the sync/backfill map onto an
  // industry (140). Writes under /api/industries take the full-clear
  // invalidation path, so the next listIndustries() is fresh.
  addIndustryAlias:    (id, alias) => apiFetch(`/api/industries/${id}/aliases`, { method: 'POST', body: JSON.stringify({ alias }) }),
  deleteIndustryAlias: (id, aliasId) => apiFetch(`/api/industries/${id}/aliases/${aliasId}`, { method: 'DELETE' }),
  getIndustryStrategics:  (id) => apiFetch(`/api/industries/${id}/strategics`),
  saveIndustryStrategics: (id, text) => apiFetch(`/api/industries/${id}/strategics`, { method: 'PUT', body: JSON.stringify({ text }) }),

  // lead sources (142) — the channel catalog, its events, and who attended.
  // Writes to these roots fall through _invalidateCache's full clear, which
  // is right: an attendance can stamp a company and a contact's referral.
  listLeadSources:   (p = {}) => apiFetch(`/api/lead-sources${qs(p)}`, { cacheMs: 5 * 60_000 }),
  createLeadSource:  (data) => apiFetch('/api/lead-sources', { method: 'POST', body: JSON.stringify(data) }),
  updateLeadSource:  (id, data) => apiFetch(`/api/lead-sources/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteLeadSource:  (id) => apiFetch(`/api/lead-sources/${id}`, { method: 'DELETE' }),
  listLeadEvents:    (p = {}) => apiFetch(`/api/lead-events${qs(p)}`, { cacheMs: 60_000 }),
  getLeadEvent:      (id) => apiFetch(`/api/lead-events/${id}`),
  createLeadEvent:   (data) => apiFetch('/api/lead-events', { method: 'POST', body: JSON.stringify(data) }),
  updateLeadEvent:   (id, data) => apiFetch(`/api/lead-events/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteLeadEvent:   (id) => apiFetch(`/api/lead-events/${id}`, { method: 'DELETE' }),
  listContactLeadEvents:   (contactId) => apiFetch(`/api/lead-events/by-contact/${contactId}`),
  upsertLeadEventAttendee: (eventId, contactId, data) => apiFetch(`/api/lead-events/${eventId}/attendees/${contactId}`, { method: 'PUT', body: JSON.stringify(data) }),
  removeLeadEventAttendee: (eventId, contactId) => apiFetch(`/api/lead-events/${eventId}/attendees/${contactId}`, { method: 'DELETE' }),
  // An event's SuperSend invite campaign(s): the drawer's funnel + replies,
  // and the Events page's "looks like an event" detector.
  leadEventCampaigns:           (eventId) => apiFetch(`/api/lead-events/${eventId}/campaigns`, { cacheMs: 0 }),
  leadEventCampaignCandidates:  (eventId) => apiFetch(`/api/lead-events/${eventId}/campaigns/candidates`, { cacheMs: 0 }),
  linkLeadEventCampaign:        (eventId, data) => apiFetch(`/api/lead-events/${eventId}/campaigns`, { method: 'POST', body: JSON.stringify(data) }),
  unlinkLeadEventCampaign:      (eventId, linkId) => apiFetch(`/api/lead-events/${eventId}/campaigns/${linkId}`, { method: 'DELETE' }),
  leadEventCampaignSuggestions: () => apiFetch('/api/lead-events/campaigns/suggestions', { cacheMs: 0 }),
  dismissLeadEventCampaign:     (campaignId) => apiFetch('/api/lead-events/campaigns/dismiss', { method: 'POST', body: JSON.stringify({ campaign_id: campaignId }) }),

  // engagements (the central deal record)
  listEngagements: (p = {}) => apiFetch(`/api/engagements${qs(withScope({ page: 1, limit: 200, ...p }))}`),
  getEngagement:   (id) => apiFetch(`/api/engagements/${id}`),
  // A deal guest's deals — the ones whose Deal team holds them (backend app/deal_guest.py).
  guestDeals:      () => apiFetch('/api/guest/deals', { cacheMs: 0 }),
  // Company signals (202609301900): the reprospect queue and a company's signals.
  // cacheMs 0: a work queue — others and the nightly job change it, and the page re-reads
  // the counts right after every Done / Dismiss / Undo (a shared in-flight read would lag).
  companySignalQueue:      (p = {}) => apiFetch(`/api/company-signals${qs(p)}`, { cacheMs: 0 }),
  companySignalSummary:    (p = {}) => apiFetch(`/api/company-signals/summary${qs(p)}`),
  // The queue under the same filters as an .xlsx (resolves to {truncated}).
  exportCompanySignals:    (p = {}) => exportFetch(`/api/company-signals/export${qs(p)}`, null, 'GET', 'signals.xlsx'),
  companySignals:          (companyId) => apiFetch(`/api/company-signals/company/${companyId}`),
  companySignal:           (id) => apiFetch(`/api/company-signals/${id}`, { cacheMs: 0 }),
  setCompanySignalStatus:  (id, status) => apiFetch(`/api/company-signals/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
  voiceOfMarket:           (p = {}) => apiFetch(`/api/company-signals/voice${qs(p)}`),
  companySignalTrends:     (p = {}) => apiFetch(`/api/company-signals/trends${qs(p)}`),
  // Deal closings (202609301300): what a Won deal closed at.
  getClosing:      (id) => apiFetch(`/api/closings/by-engagement/${id}`),
  saveClosing:     (id, data) => apiFetch(`/api/closings/by-engagement/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  listClosings:    (p = {}) => apiFetch(`/api/closings${qs(p)}`),
  closingsStats:   (p = {}) => apiFetch(`/api/closings/stats${qs(p)}`),
  // LOI-to-close timeline (202611021430): baseline, milestone targets, projected slip.
  getCloseTimeline: (id) => apiFetch(`/api/engagements/${id}/close-timeline`, { cacheMs: 0 }),
  setMilestone:     (id, key, completed_on) => apiFetch(`/api/engagements/${id}/milestones/${key}`, { method: 'PUT', body: JSON.stringify({ completed_on }) }),
  // Fireflies soundbites flagged in this deal's meetings (Highlights section).
  engagementHighlights: (id) => apiFetch(`/api/engagements/${id}/highlights`),
  // The automatic next action's trail ({auto: null} once a person edits it).
  engagementNextActionAuto: (id) => apiFetch(`/api/engagements/${id}/next-action-auto`),
  // Talk-time + sentiment per recorded meeting (Call analytics section).
  engagementCallAnalytics: (id) => apiFetch(`/api/engagements/${id}/call-analytics`),
  // Live-meeting presence — a poll target, so bypass the GET cache.
  engagementLiveMeeting: (id) => apiFetch(`/api/engagements/${id}/live-meeting`, { cacheMs: 0 }),
  // Pinned deals — per-user sidebar pins. Writes invalidate the GET cache
  // via the standard non-GET invalidation, so the sidebar refetch sees truth.
  listPinnedDeals: () => apiFetch('/api/engagements/pins'),
  pinDeal:         (id) => apiFetch(`/api/engagements/${id}/pin`, { method: 'POST' }),
  unpinDeal:       (id) => apiFetch(`/api/engagements/${id}/pin`, { method: 'DELETE' }),
  reorderPinnedDeals: (ids) => apiFetch('/api/engagements/pins/order', { method: 'PUT', body: JSON.stringify({ ids }) }),
  createEngagement:(data) => apiFetch('/api/engagements', { method: 'POST', body: JSON.stringify(data) }),
  updateEngagement:(id, data) => apiFetch(`/api/engagements/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteEngagement:(id) => apiFetch(`/api/engagements/${id}`, { method: 'DELETE' }),

  // pipeline
  listStages:  () => apiFetch('/api/pipeline/stages', { cacheMs: 5 * 60_000 }),
  createStage: (data) => apiFetch('/api/pipeline/stages', { method: 'POST', body: JSON.stringify(data) }),
  updateStage: (id, data) => apiFetch(`/api/pipeline/stages/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteStage: (id, hard = false) => apiFetch(`/api/pipeline/stages/${id}${hard ? '?hard=true' : ''}`, { method: 'DELETE' }),
  createStageExit: (stageId, data) => apiFetch(`/api/pipeline/stages/${stageId}/exits`, { method: 'POST', body: JSON.stringify(data) }),
  updateStageExit: (stageId, exitId, data) => apiFetch(`/api/pipeline/stages/${stageId}/exits/${exitId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteStageExit: (stageId, exitId) => apiFetch(`/api/pipeline/stages/${stageId}/exits/${exitId}`, { method: 'DELETE' }),

  // field definitions + on-demand fill
  listFieldDefinitions: (object_type) => apiFetch(`/api/field-definitions${qs({ object_type })}`, { cacheMs: 5 * 60_000 }),
  createFieldDefinition: (data) => apiFetch('/api/field-definitions', { method: 'POST', body: JSON.stringify(data) }),
  updateFieldDefinition: (id, data) => apiFetch(`/api/field-definitions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteFieldDefinition: (id) => apiFetch(`/api/field-definitions/${id}`, { method: 'DELETE' }),
  fillField: (object_type, record_id, field_key) =>
    apiFetch(`/api/field-definitions/fill/${object_type}/${record_id}/${field_key}`, { method: 'POST' }),
  bulkFillField: (field_id, p = {}) =>
    apiFetch(`/api/field-definitions/${field_id}/bulk-fill${qs({ max_records: 50, skip_already_filled: true, cost_cap_usd: 5.0, offset: 0, ...p })}`, { method: 'POST' }),
  previewFormula: (data) =>
    apiFetch('/api/field-definitions/preview-formula', { method: 'POST', body: JSON.stringify(data) }),
  startBulkFillJob: (field_id, data) =>
    apiFetch(`/api/field-definitions/${field_id}/bulk-fill-job`, { method: 'POST', body: JSON.stringify(data) }),
  listBulkFillJobs: () => apiFetch('/api/bulk-fill-jobs'),
  getBulkFillJob: (id) => apiFetch(`/api/bulk-fill-jobs/${id}`),
  cancelBulkFillJob: (id) =>
    apiFetch(`/api/bulk-fill-jobs/${id}/cancel`, { method: 'POST' }),

  // research wand — one field, one run (backend app/field_research.py).
  // No GET cache: a run changes every second while it's in flight.
  researchForRecord: (object_type, record_id) => apiFetch(`/api/research${qs({ object_type, record_id })}`, { cacheMs: 0 }),
  startResearch: (data) => apiFetch('/api/research', { method: 'POST', body: JSON.stringify(data) }),
  getResearch: (id) => apiFetch(`/api/research/${id}`, { cacheMs: 0 }),
  markResearchApplied: (id) => apiFetch(`/api/research/${id}/applied`, { method: 'POST' }),
  dismissResearch: (id) => apiFetch(`/api/research/${id}/dismiss`, { method: 'POST' }),
  // Settings → Costs (admins only)
  costs: (days = 30) => apiFetch(`/api/costs${qs({ days })}`, { cacheMs: 0 }),

  // company patches (alongside the older create helper)
  updateCompany: (id, data) => apiFetch(`/api/companies/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  // "Not acquired": what undoing a company's acquisition would change (live —
  // never cached), and doing it. The note is required (422 note_required).
  getAcquisitionReversal: (id) => apiFetch(`/api/companies/${id}/acquisition-reversal`, { cacheMs: 0 }),
  reverseAcquisition: (id, data) => apiFetch(`/api/companies/${id}/acquisition-reversal`, { method: 'POST', body: JSON.stringify(data) }),
  deleteCompany: (id) => apiFetch(`/api/companies/${id}`, { method: 'DELETE' }),
  bulkPromoteCompanies: (data) => apiFetch('/api/companies/bulk-promote', { method: 'POST', body: JSON.stringify(data) }),

  // contact patches
  updateContact: (id, data) => apiFetch(`/api/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteContact: (id) => apiFetch(`/api/contacts/${id}`, { method: 'DELETE' }),

  // opportunities (atlas calls them "deals")
  listOpportunities: (p = {}) => apiFetch(`/api/opportunities${qs({ page: 1, limit: 200, ...p })}`),
  getOpportunity: (id) => apiFetch(`/api/opportunities/${id}`),

  // agents
  agentCatalog: () => apiFetch('/api/agents/catalog', { cacheMs: 5 * 60_000 }),
  lostDealsRuns: () => apiFetch('/api/agents/lost-deals/runs'),
  createLostDealsRun: ({ days, since, until } = {}) => {
    const p = new URLSearchParams();
    if (since) { p.set('since', since); if (until) p.set('until', until); }
    else p.set('days', days || 90);
    return apiFetch(`/api/agents/lost-deals/runs?${p}`, { method: 'POST' });
  },
  lostDealsResults: (id) => apiFetch(`/api/agents/lost-deals/runs/${id}/results`),
  lostDealsFindings: (status) =>
    apiFetch(`/api/agents/lost-deals/findings${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  lostDealsRowAction: (id, rowIndex, body) =>
    apiFetch(`/api/agents/lost-deals/runs/${id}/rows/${rowIndex}/action`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  handoffScanRuns: (memberId) =>
    apiFetch(`/api/agents/handoff-scan/runs?member_id=${encodeURIComponent(memberId)}`),
  createHandoffScan: (memberId, days = 90) =>
    apiFetch(`/api/agents/handoff-scan/runs?member_id=${encodeURIComponent(memberId)}&days=${days}`, { method: 'POST' }),
  handoffScanResults: (id) => apiFetch(`/api/agents/handoff-scan/runs/${id}/results`),
  agentWorkspaces: (p = {}) => apiFetch(`/api/agent-workspaces${qs(p)}`),
  getAgentWorkspace: (id) => apiFetch(`/api/agent-workspaces/${id}`),
  createAgentRun: (workspaceId, data) =>
    apiFetch(`/api/agent-workspaces/${workspaceId}/runs`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // On-demand DealCloud re-sync for a single record
  listDcOutbox:  (status) => apiFetch(`/api/sync/outbox${status ? `?status=${status}` : ''}`),
  retryDcOutbox: (id) => apiFetch(`/api/sync/outbox/${id}/retry`, { method: 'POST' }),
  syncEngagementNow: (id) => apiFetch(`/api/engagements/${id}/sync`, { method: 'POST' }),
  syncCompanyNow:    (id) => apiFetch(`/api/companies/${id}/sync`,    { method: 'POST' }),
  syncContactNow:    (id) => apiFetch(`/api/contacts/${id}/sync`,     { method: 'POST' }),

  // Emails page (routes/marketing_board.py) — the weekly send report, the
  // PSAC board + queues.
  marketingBoard:  (p = {}) => apiFetch(`/api/marketing/board${qs(p)}`),
  marketingBoardCounts: (p = {}) => apiFetch(`/api/marketing/board/counts${qs(p)}`),
  marketingTable:  (p = {}) => apiFetch(`/api/marketing/table${qs(p)}`),
  marketingQueues: (p = {}) => apiFetch(`/api/marketing/queues${qs(p)}`),
  marketingCleaning: (p = {}) => apiFetch(`/api/marketing/cleaning${qs(p)}`),
  marketingEmailReport: (p = {}) => apiFetch(`/api/marketing/email-report${qs(p)}`),
  setMarketingStatus: (contactId, body) =>
    apiFetch(`/api/marketing/contacts/${contactId}/status`, { method: 'POST', body: JSON.stringify(body) }),
  contactPsac:      (contactId) => apiFetch(`/api/marketing/contacts/${contactId}/psac`),
  addContactToPsac: (contactId) => apiFetch(`/api/marketing/contacts/${contactId}/psac`, { method: 'POST' }),
  contactOutreach:  (contactId) => apiFetch(`/api/marketing/contacts/${contactId}/outreach`),

  // PB/PEG blast manager (routes/blast_board.py).
  blastBoard:        () => apiFetch('/api/blast/board'),
  blastEmail:        (list) => apiFetch(`/api/blast/email?list=${list}`),
  blastQueue:        (body) => apiFetch('/api/blast/queue', { method: 'POST', body: JSON.stringify(body) }),
  blastRemoveEntry:  (entryId, reason) =>
    apiFetch(`/api/blast/queue/${entryId}/remove`, { method: 'POST', body: JSON.stringify({ reason }) }),
  blastCompanyState: (companyId) => apiFetch(`/api/blast/company/${companyId}`),
  blastMarkSent:     (list, batchId) => apiFetch('/api/blast/mark-sent', { method: 'POST', body: JSON.stringify({ list_type: list, ...(batchId ? { batch_id: batchId } : {}) }) }),
  blastPush:         (list) => apiFetch('/api/blast/push', { method: 'POST', body: JSON.stringify({ list_type: list }) }),
  blastBatches:      (list) => apiFetch(`/api/blast/batches${list ? `?list=${list}` : ''}`),
  blastBatch:        (id) => apiFetch(`/api/blast/batches/${id}`),
  blastConfig:       () => apiFetch('/api/blast/config'),
  blastSaveConfig:   (body) => apiFetch('/api/blast/config', { method: 'PUT', body: JSON.stringify(body) }),
  blastCampaigns:    () => apiFetch('/api/blast/campaigns'),
  blastHoldout:      (list) => apiFetch(`/api/blast/holdout?list=${list}`, { cacheMs: 0 }),
  blastHoldoutJob:   (list) => apiFetch(`/api/blast/holdout/job?list=${list}`, { cacheMs: 0 }),
  // A deal's blast-to-network toggle + do-not-contact list (routes/blast_network.py).
  blastNetwork:      (engagementId) => apiFetch(`/api/blast/network/${engagementId}`, { cacheMs: 0 }),
  blastNetworkSet:   (engagementId, on) => apiFetch(`/api/blast/network/${engagementId}`, { method: 'PUT', body: JSON.stringify({ blast_to_network: on }) }),
  blastDncAdd:       (engagementId, body) => apiFetch(`/api/blast/network/${engagementId}/dnc`, { method: 'POST', body: JSON.stringify(body) }),
  blastDncUpdate:    (engagementId, id, body) => apiFetch(`/api/blast/network/${engagementId}/dnc/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  blastDncRemove:    (engagementId, id) => apiFetch(`/api/blast/network/${engagementId}/dnc/${id}`, { method: 'DELETE' }),

  // raw
  fetch: apiFetch,
};

window.AtlasApi = AtlasApi;
